import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import { GoalForm } from '../../components/workspace/performance/GoalForm';
import { SearchableSelect } from '../../components/workspace/filters/FilterDropdowns';
import { formatDate } from '../../lib/format';
import Modal from '../../components/Modal';
import { CreateAppraisalModal, AppraisalDetail } from './AppraisalDashboard';
import PerformanceReviewCycles from './PerformanceReviewCycles';
import WorkspaceSettings from './WorkspaceSettings';

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_COLOR = {
  pending:          '#f0a93b',
  submitted:        '#6f7bff',
  approved:         '#4ad295',
  rejected:         '#ff6b6b',
  pending_self:     '#f0a93b',
  pending_manager:  '#6f7bff',
  pending_approver: '#06b6d4',
  closed:           '#8a94a6',
};

const REVIEW_STATUS_LABEL = {
  pending_self:     'Self Rating Pending',
  pending_manager:  'With Manager',
  pending_approver: 'Awaiting Final Approval',
  approved:         'Approved',
  rejected:         'Rejected',
};

const GOAL_STATUS_LABEL = {
  pending:   'Draft',
  submitted: 'Awaiting Approval',
  approved:  'Approved',
  rejected:  'Rejected',
};

const fmtStatus = s => (s || '').split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const BADGE_META = {
  teamwork:        { emoji: '🤝', label: 'Teamwork' },
  innovation:      { emoji: '💡', label: 'Innovation' },
  leadership:      { emoji: '🌟', label: 'Leadership' },
  excellence:      { emoji: '🏆', label: 'Excellence' },
  mentorship:      { emoji: '🎓', label: 'Mentorship' },
  customer_focus:  { emoji: '💛', label: 'Customer Focus' },
  problem_solving: { emoji: '🔧', label: 'Problem Solving' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function userName(user) {
  if (!user) return 'Unknown';
  return [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email;
}

function initials(user) {
  const name = userName(user);
  return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
}

// ─── UI Primitives ────────────────────────────────────────────────────────────

function StatusPill({ status, label }) {
  const color = STATUS_COLOR[status] || 'var(--text-secondary)';
  const text  = label || GOAL_STATUS_LABEL[status] || REVIEW_STATUS_LABEL[status] || fmtStatus(status);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', borderRadius: 20,
      fontSize: 12, fontWeight: 600,
      background: color + '18', color, justifySelf: 'start',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, display: 'inline-block' }} />
      {text}
    </span>
  );
}

function Avatar({ user, size = 36, color = '#6f7bff' }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: color, color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.38, fontWeight: 700, flexShrink: 0,
      letterSpacing: 0.5,
    }}>
      {initials(user) || '?'}
    </div>
  );
}

function Card({ children, accent, style = {} }) {
  return (
    <div style={{
      background: 'var(--bg-card)',
      border: '1px solid var(--border)',
      borderRadius: 10,
      borderLeft: accent ? `4px solid ${accent}` : undefined,
      padding: '16px 20px',
      marginBottom: 10,
      ...style,
    }}>
      {children}
    </div>
  );
}

function EmptyState({ icon, title, subtitle }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--text-muted)' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>{icon}</div>
      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>{title}</div>
      {subtitle && <div style={{ fontSize: 13 }}>{subtitle}</div>}
    </div>
  );
}

function SectionTitle({ title, subtitle, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
      <div>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</h2>
        {subtitle && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 3 }}>{subtitle}</div>}
      </div>
      {action}
    </div>
  );
}

function FilterRow({ children }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 16 }}>
      {children}
    </div>
  );
}

function Select({ label, value, onChange, children, disabled }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {label && <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>}
      <select
        value={value || ''}
        onChange={onChange}
        disabled={disabled}
        style={{
          border: '1px solid var(--border)', borderRadius: 7,
          padding: '7px 10px', fontSize: 13, background: 'var(--bg-card)',
          color: 'var(--text-primary)', cursor: disabled ? 'not-allowed' : 'pointer',
          outline: 'none', minWidth: 150,
        }}
      >
        {children}
      </select>
    </div>
  );
}

// ─── Shared helper ────────────────────────────────────────────────────────────

// Returns how many rows fit in the viewport for a given row height and fixed overhead.
// overhead = px consumed by page chrome above the list (header, tabs, filters, etc.)
function usePageSize(rowHeight, overhead = 360) {
  const [size, setSize] = useState(() => Math.max(5, Math.floor((window.innerHeight - overhead) / rowHeight)));
  useEffect(() => {
    const update = () => setSize(Math.max(5, Math.floor((window.innerHeight - overhead) / rowHeight)));
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [rowHeight, overhead]);
  return size;
}

function daysPending(createdAt) {
  if (!createdAt) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(createdAt)) / 86400000));
}

function DaysBadge({ days }) {
  if (days === null || days === undefined) return <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>—</span>;
  const color = days > 14 ? '#ff6b6b' : days > 7 ? '#f0a93b' : '#4ad295';
  return (
    <span style={{
      fontWeight: 700, fontSize: 13, color,
      background: color + '12', padding: '3px 9px', borderRadius: 8,
      display: 'inline-block', justifySelf: 'start',
    }}>
      {days}d
    </span>
  );
}

// ─── Overview (hero dashboard) ────────────────────────────────────────────────

const TEAL = '#2dd4bf';

// Area chart for rating trend — stretches to fill container for small teams, scrollable for large
function OvwSparkLine({ data }) {
  if (!data.length) return (
    <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:6, color:'var(--text-muted)', minHeight:160 }}>
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.3">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
      </svg>
      <span style={{ fontSize:12, textAlign:'center', lineHeight:1.5, opacity:0.6 }}>No data yet</span>
    </div>
  );

  const n = data.length;
  const large = n > 15;
  // Fixed viewBox coordinates — SVG scales uniformly via width="100%"
  // Use generous spacing so labels never overlap axes
  const colW = large ? 80 : Math.max(100, 700 / Math.max(n, 1));
  const PAD  = { t: 36, r: 32, b: 80, l: 28 };
  const chartW = n <= 1 ? colW : colW * (n - 1);
  const W = PAD.l + chartW + PAD.r;
  const H = 220;
  const chartH = H - PAD.t - PAD.b;
  const maxV = 5;

  const xOf = i => PAD.l + (n <= 1 ? chartW / 2 : (i / (n - 1)) * chartW);
  const yOf = v => PAD.t + chartH - (v / maxV) * chartH;

  const pts = data.map((d, i) => [xOf(i), yOf(d.score)]);
  const linePath = `M${pts[0][0]},${pts[0][1]} ` + pts.slice(1).map(([x, y]) => `L${x},${y}`).join(' ');
  const fillPath = `${linePath} L${pts[n-1][0]},${PAD.t + chartH} L${pts[0][0]},${PAD.t + chartH} Z`;
  const latestIdx = n - 1;

  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', minHeight:160, overflowX: large ? 'auto' : 'hidden', overflowY:'visible' }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width={large ? W : '100%'}
        height="100%"
        preserveAspectRatio="xMidYMid meet"
        style={{ display:'block', minWidth: large ? W : '100%', overflow:'visible' }}>
        <defs>
          <linearGradient id="ovw-area-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={TEAL} stopOpacity="0.28"/>
            <stop offset="100%" stopColor={TEAL} stopOpacity="0.02"/>
          </linearGradient>
        </defs>

        {/* Gridlines + Y labels — positioned well left of chart area */}
        {[1, 2, 3, 4, 5].map(v => (
          <g key={v}>
            <line x1={PAD.l} y1={yOf(v)} x2={W - PAD.r} y2={yOf(v)}
              stroke="var(--border)" strokeWidth="1" strokeDasharray="4,4"/>
            <text x={PAD.l - 6} y={yOf(v)} fontSize="11" fill="var(--text-muted)"
              textAnchor="end" dominantBaseline="middle">{v}</text>
          </g>
        ))}

        {/* Area + Line */}
        <path d={fillPath} fill="url(#ovw-area-grad)"/>
        <path d={linePath} fill="none" stroke={TEAL} strokeWidth="2.5"
          strokeLinejoin="round" strokeLinecap="round"/>

        {/* Dots + score labels above dot + name labels below axis */}
        {pts.map(([x, y], i) => (
          <g key={i}>
            <circle cx={x} cy={y}
              r={i === latestIdx ? 6 : 4}
              fill={i === latestIdx ? TEAL : 'var(--bg-card)'}
              stroke={TEAL} strokeWidth="2"/>
            {/* Score above dot — offset enough to clear the dot */}
            <text x={x} y={y - 13} textAnchor="middle" fontSize="11" fontWeight="700"
              fill={TEAL}>{data[i].score.toFixed(2)}</text>
            {/* Name below x-axis, rotated -40deg, anchored at right end of text */}
            <text
              x={x} y={PAD.t + chartH + 12}
              fontSize="11" fill="var(--text-muted)"
              textAnchor="end"
              transform={`rotate(-40, ${x}, ${PAD.t + chartH + 12})`}>
              {data[i].label}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

// Animated donut ring for latest score
function ScoreRing({ score, max = 5 }) {
  const R = 40, cx = 48, cy = 48, stroke = 6;
  const circ = 2 * Math.PI * R;
  const pct  = score != null ? Math.min(1, score / max) : 0;
  const color = score == null ? 'var(--border-strong)'
    : score >= 4 ? '#4ad295'
    : score >= 3 ? TEAL
    : score >= 2 ? '#f0a93b'
    : '#ff6b6b';
  return (
    <svg width={96} height={96} viewBox="0 0 96 96">
      <circle cx={cx} cy={cy} r={R} fill="none" stroke="var(--border)" strokeWidth={stroke}/>
      {score != null && (
        <circle cx={cx} cy={cy} r={R} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={`${circ * pct} ${circ}`}
          strokeDashoffset={circ * 0.25}
          strokeLinecap="round"
        />
      )}
      <text x={cx} y={cy - 3} textAnchor="middle" fontSize="20" fontWeight="800"
        fill={score != null ? color : 'var(--text-muted)'} fontFamily="inherit">
        {score != null ? score.toFixed(2) : '—'}
      </text>
      <text x={cx} y={cy + 13} textAnchor="middle" fontSize="9" fill="var(--text-muted)" fontFamily="inherit">/ {max}.0</text>
    </svg>
  );
}

// Inline link button — no bg, no border, just colored text
function LinkBtn({ onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{ background:'none', border:'none', color:TEAL, fontSize:12, fontWeight:600, cursor:'pointer', padding:0, flexShrink:0 }}
    >
      {children}
    </button>
  );
}

// Card header row
function CardHead({ title, sub, action }) {
  return (
    <div style={{ padding:'14px 18px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
      <div>
        <div style={{ fontSize:14, fontWeight:700, color:'var(--text-primary)' }}>{title}</div>
        {sub && <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>{sub}</div>}
      </div>
      {action}
    </div>
  );
}

function Overview({ appId, onNavigate, onGoalsBadge }) {
  const api      = useApi();
  const navigate = useNavigate();
  const { myRole } = useWorkspace();

  const isManager    = myRole === 'admin' || myRole === 'super_admin';
  const isSuperAdmin = myRole === 'super_admin';

  // scope: 'mine' | 'direct' | 'team' | 'all'
  const [scope, setScope] = useState('mine');

  const [loading,          setLoading]          = useState(true);
  const [teamLoading,      setTeamLoading]      = useState(false);
  const [myReviews,        setMyReviews]        = useState([]);
  const [goalsList,        setGoalsList]        = useState([]);
  const [myPendingItems,   setMyPendingItems]   = useState([]);
  const [pendingAppr,      setPendingAppr]      = useState([]);
  const [teamReviews,      setTeamReviews]      = useState([]);
  const [teamPendingItems, setTeamPendingItems] = useState([]);
  const [teamSize,         setTeamSize]         = useState(0);

  const uname = u => u ? ([u.first_name, u.last_name].filter(Boolean).join(' ') || u.email) : '—';

  // Load personal + goals + pending (once per appId)
  useEffect(() => {
    setLoading(true);
    Promise.allSettled([
      api.listMyReviews(appId),
      api.listGoals(appId),
      isManager ? api.listPendingForMe(appId) : Promise.resolve({ items: [] }),
      api.listAppraisals(appId, { view: 'mine' }).catch(() => ({ items: [] })),
    ]).then(([reviewsR, goalsR, finalR, apprR]) => {
      const reviews = reviewsR.status === 'fulfilled' ? (reviewsR.value.items   || []) : [];
      const goals   = goalsR.status   === 'fulfilled' ? (goalsR.value.items     || []) : [];
      const final   = finalR.status   === 'fulfilled' ? (finalR.value.items     || []) : [];
      const appr    = apprR.status    === 'fulfilled' ? (apprR.value?.items     || []) : [];

      setMyReviews(reviews);
      setGoalsList(goals);
      onGoalsBadge?.(goals.length);

      if (!isManager) {
        setMyPendingItems(reviews.filter(r => r.status === 'pending_self'));
      } else {
        const approverPending = final.filter(r => r.status === 'pending_approver');
        setMyPendingItems(approverPending);
      }
      setPendingAppr(appr.filter(a => !['completed', 'rejected', 'closed'].includes(a.status)));
    }).finally(() => setLoading(false));
  }, [appId, isManager]);

  // Load team reviews whenever scope changes (for managers)
  useEffect(() => {
    if (!isManager || scope === 'mine') {
      setTeamReviews([]);
      setTeamPendingItems([]);
      setTeamSize(0);
      return;
    }
    setTeamLoading(true);
    api.listTeamReviews(appId, { scope })
      .then(d => {
        const team = d.items || [];
        setTeamReviews(team);
        const uniqueMembers = new Set(team.map(r => (r.user?.id || r.employee?.id || r.platform_user_id)).filter(Boolean));
        setTeamSize(uniqueMembers.size);
        setTeamPendingItems(team.filter(r => r.status === 'pending_manager'));
      })
      .catch(() => { setTeamReviews([]); setTeamPendingItems([]); })
      .finally(() => setTeamLoading(false));
  }, [appId, isManager, scope]);

  const buildRatingHistory = (reviews) =>
    [...reviews]
      .filter(r => r.status === 'approved' && r.final_score != null)
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      .slice(-6)
      .map(r => ({
        label: (r.cycle_name || `#${r.cycle_id}`).replace(/\s+/g, '·').slice(0, 7),
        score: parseFloat(r.final_score),
        userId: r.platform_user_id,
        userName: uname(r.user || r.employee),
      }));

  const buildManagerRatingHistory = (teamRevs) => {
    const byUser = {};
    for (const r of teamRevs) {
      if (r.status !== 'approved' || r.final_score == null) continue;
      const uid = r.platform_user_id;
      if (!byUser[uid] || new Date(r.created_at) > new Date(byUser[uid].created_at)) {
        byUser[uid] = r;
      }
    }
    return Object.values(byUser)
      .sort((a, b) => parseFloat(a.final_score) - parseFloat(b.final_score))
      .map(r => ({
        label: uname(r.user || r.employee),
        score: parseFloat(r.final_score),
      }));
  };

  const myRatingHistory   = buildRatingHistory(myReviews);
  const teamRatingHistory = buildManagerRatingHistory(teamReviews);

  // Display logic based on scope
  const showingTeam = isManager && scope !== 'mine';
  const pendingItems = showingTeam ? teamPendingItems : myPendingItems;
  const ratingHistory = showingTeam ? teamRatingHistory : myRatingHistory;
  const trendLabel = showingTeam
    ? (scope === 'direct' ? 'Direct Reportees Rating (Latest)' : scope === 'all' ? 'All Members Rating (Latest)' : 'Entire Team Rating (Latest)')
    : 'My Rating Trend';

  const latestScore = ratingHistory.length ? ratingHistory[ratingHistory.length - 1].score : null;
  const prevScore   = ratingHistory.length > 1 ? ratingHistory[ratingHistory.length - 2].score : null;
  const scoreDelta  = latestScore != null && prevScore != null ? latestScore - prevScore : null;

  const teamAvgScore = teamRatingHistory.length
    ? Math.round((teamRatingHistory.reduce((s, r) => s + r.score, 0) / teamRatingHistory.length) * 100) / 100
    : null;

  const latestScoreLabel = showingTeam
    ? (scope === 'direct' ? 'Direct Avg' : scope === 'all' ? 'Org Avg' : 'Team Avg')
    : 'My Latest Rating';
  const displayScore = showingTeam ? teamAvgScore : latestScore;

  const goalGroups = {
    approved:  goalsList.filter(g => g.status === 'approved'),
    submitted: goalsList.filter(g => g.status === 'submitted'),
    pending:   goalsList.filter(g => g.status === 'pending'),
    rejected:  goalsList.filter(g => g.status === 'rejected'),
  };
  const goalGroupMeta = [
    { key: 'approved',  label: 'Approved',         color: '#4ad295' },
    { key: 'submitted', label: 'Awaiting Approval', color: '#6f7bff' },
    { key: 'pending',   label: 'Draft',             color: '#f0a93b' },
    { key: 'rejected',  label: 'Rejected',          color: '#ff6b6b' },
  ];

  const pendingManagerCount  = teamReviews.filter(r => r.status === 'pending_manager').length;
  const pendingApproverCount = teamReviews.filter(r => r.status === 'pending_approver').length;
  const completedTeamCount   = teamReviews.filter(r => r.status === 'approved').length;

  const APPR_STATUS_LABEL = {
    pending_employee: 'Employee Pending',
    pending_manager:  'Manager Review',
    pending_approver: 'Final Approval',
    completed:        'Completed',
    rejected:         'Rejected',
  };

  if (loading) return (
    <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
      {[180, 60, 200].map((h, i) => (
        <div key={i} style={{ height:h, borderRadius:12, background:'var(--bg-card)', border:'1px solid var(--border)', opacity:0.4 }}/>
      ))}
    </div>
  );

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:12 }}>

      {/* ── Scope dropdown (managers only) ───────────────────── */}
      {isManager && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'end', gap:8 }}>
          <select
            value={scope}
            onChange={e => setScope(e.target.value)}
            className="tdb-scope-select"
          >
            <option value="mine">My Performance</option>
            <option value="direct">Direct Reportees</option>
            <option value="team">Entire Team</option>
            {isSuperAdmin && <option value="all">All Members</option>}
          </select>
          {teamLoading && <span style={{ fontSize:12, color:'var(--text-muted)' }}>Loading…</span>}
        </div>
      )}

      {/* ── Hero card ─────────────────────────────────────── */}
      {(() => {
        const showTrend = !showingTeam && ratingHistory.length >= 2;
        const cols = '1fr 220px 160px';
        return (
      <div style={{
        display:'grid',
        gridTemplateColumns: cols,
        background:'var(--bg-card)',
        border:'1px solid var(--border)',
        borderRadius:12,
        overflow:'hidden',
        height:260,
      }}>
        {/* Center panel: member list for team scopes, score breakdown for mine scope */}
        {showingTeam ? (
          <div style={{ borderRight:'1px solid var(--border)', display:'flex', flexDirection:'column' }}>
            <div style={{ padding:'16px 20px 10px', fontSize:9, fontWeight:700, letterSpacing:1.2, color:'var(--text-muted)', textTransform:'uppercase', flexShrink:0 }}>
              {trendLabel}
            </div>
            <div style={{ flex:1, overflowY:'auto', padding:'0 20px 12px' }}>
              {teamLoading ? (
                <div style={{ fontSize:12, color:'var(--text-muted)', textAlign:'center', padding:'20px 0' }}>Loading…</div>
              ) : teamRatingHistory.length === 0 ? (
                <div style={{ fontSize:12, color:'var(--text-muted)', textAlign:'center', padding:'20px 0' }}>No approved reviews yet</div>
              ) : teamRatingHistory.map((m, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'7px 0', borderBottom:'1px solid var(--border)' }}>
                  <div style={{ width:26, height:26, borderRadius:'50%', background:'var(--bg-muted)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, color:'var(--text-muted)', flexShrink:0 }}>
                    {m.label.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ fontSize:13, fontWeight:500, color:'var(--text-primary)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', flexShrink:0, maxWidth:120 }}>
                    {m.label}
                  </div>
                  <div style={{ flex:1, height:5, borderRadius:4, background:'var(--border)', minWidth:0 }}>
                    <div style={{ height:'100%', borderRadius:4, width:`${(m.score/5)*100}%`, background: TEAL }}/>
                  </div>
                  <div style={{ fontSize:14, fontWeight:800, color: TEAL, flexShrink:0 }}>
                    {m.score.toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (() => {
          // Member: show Goals / Skills / Values breakdown from latest approved review
          const latestApproved = [...myReviews]
            .filter(r => r.status === 'approved')
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
          const breakdown = latestApproved ? [
            { label: 'Goals',  score: latestApproved.goals_score  != null ? parseFloat(latestApproved.goals_score)  : null, color: '#6f7bff' },
            { label: 'Skills', score: latestApproved.skills_score != null ? parseFloat(latestApproved.skills_score) : null, color: TEAL },
            { label: 'Values', score: latestApproved.values_score != null ? parseFloat(latestApproved.values_score) : null, color: '#4ad295' },
          ] : [];
          return (
            <div style={{ padding:'20px 24px', borderRight:'1px solid var(--border)', display:'flex', flexDirection:'column', justifyContent:'center', gap:14 }}>
              <div style={{ fontSize:9, fontWeight:700, letterSpacing:1.2, color:'var(--text-muted)', textTransform:'uppercase' }}>
                {latestApproved ? `Latest Review · ${latestApproved.cycle_name || `Cycle #${latestApproved.cycle_id}`}` : 'Score Breakdown'}
              </div>
              {breakdown.length === 0 ? (
                <div style={{ fontSize:12, color:'var(--text-muted)', textAlign:'center' }}>No approved reviews yet</div>
              ) : breakdown.map(({ label, score, color }) => (
                <div key={label}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5 }}>
                    <span style={{ fontSize:12, fontWeight:600, color:'var(--text-secondary)' }}>{label}</span>
                    <span style={{ fontSize:13, fontWeight:800, color: score != null ? color : 'var(--text-muted)' }}>
                      {score != null ? score.toFixed(2) : '—'}
                    </span>
                  </div>
                  <div style={{ height:6, borderRadius:4, background:'var(--border)' }}>
                    <div style={{ height:'100%', borderRadius:4, width: score != null ? `${(score / 5) * 100}%` : '0%', background: color, transition:'width 0.4s ease' }}/>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        {/* Action required */}
        <div style={{ padding:'16px 16px', borderRight:'1px solid var(--border)', display:'flex', flexDirection:'column', justifyContent:'center', gap:8 }}>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:1.2, color:'var(--text-muted)', textTransform:'uppercase' }}>
            {pendingItems.length > 0 ? `Action Required · ${pendingItems.length}` : 'Action Required'}
          </div>
          {pendingItems.length > 0 ? (() => {
            const r = pendingItems[0];
            const sc = STATUS_COLOR[r.status] || '#f0a93b';
            return (
              <>
                <div style={{
                  display:'flex', alignItems:'center', gap:8,
                  padding:'8px 10px', borderRadius:8,
                  background:'var(--bg-muted)',
                  border:`1px solid ${sc}25`,
                }}>
                  <div style={{ width:32, height:32, borderRadius:7, background:`${sc}16`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, flexShrink:0 }}>
                    {isManager ? '👤' : '📋'}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:12, fontWeight:600, color:'var(--text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {isManager ? uname(r.user || r.employee) : (r.cycle_name || 'Review')}
                    </div>
                    <StatusPill status={r.status}/>
                  </div>
                </div>
                {pendingItems.length > 1 && (
                  <div style={{ fontSize:10, color:'var(--text-muted)', textAlign:'center' }}>+{pendingItems.length - 1} more pending</div>
                )}
                <button
                  onClick={() => isManager ? onNavigate('mywork') : navigate(`/apps/${appId}/performance/reviews/${r.id}`)}
                  style={{ padding:'8px 0', borderRadius:8, border:'none', background:TEAL, color:'#0f172a', fontWeight:700, fontSize:12, cursor:'pointer' }}
                >
                  {isManager ? 'Review Now →' : 'Continue →'}
                </button>
              </>
            );
          })() : (
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6, padding:'8px 0', color:'var(--text-muted)', textAlign:'center' }}>
              <div style={{ width:40, height:40, borderRadius:12, background:'rgba(74,210,149,0.1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 }}>✅</div>
              <span style={{ fontSize:12 }}>All caught up!</span>
            </div>
          )}
        </div>

        {/* Score ring */}
        <div style={{
          padding:'20px 16px',
          display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:8,
          background:'linear-gradient(160deg, var(--bg-card) 0%, var(--bg-muted) 100%)',
        }}>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:1.2, color:'var(--text-muted)', textTransform:'uppercase' }}>
            {latestScoreLabel}
          </div>
          <ScoreRing score={displayScore}/>
          {scoreDelta != null ? (
            <span style={{
              display:'inline-flex', alignItems:'center', gap:3, fontSize:10, fontWeight:700,
              padding:'2px 8px', borderRadius:20,
              background: scoreDelta >= 0 ? 'rgba(74,210,149,0.14)' : 'rgba(255,107,107,0.14)',
              color: scoreDelta >= 0 ? '#4ad295' : '#ff6b6b',
            }}>
              {scoreDelta >= 0 ? '▲' : '▼'} {Math.abs(scoreDelta).toFixed(1)} vs prev
            </span>
          ) : displayScore == null ? (
            <span style={{ fontSize:10, color:'var(--text-muted)', textAlign:'center', lineHeight:1.4 }}>No approved reviews yet</span>
          ) : null}
        </div>
      </div>
        );
      })()}

      {/* ── Manager stats strip ───────────────────────────── */}
      {isManager && showingTeam && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:10 }}>
          {[
            { label:'Team Members',      value: teamSize,             icon:'👥', color:'#6f7bff' },
            { label:'Pending My Review', value: pendingManagerCount,  icon:'⏳', color:'#f0a93b' },
            { label:'Pending Approval',  value: pendingApproverCount, icon:'📋', color:'#06b6d4' },
            { label:'Team Completed',    value: completedTeamCount,   icon:'✅', color:'#4ad295' },
          ].map(({ label, value, icon, color }) => (
            <div key={label}
              onClick={() => label !== 'Team Members' && onNavigate('mywork')}
              onMouseEnter={e => { if(label !== 'Team Members') e.currentTarget.style.background='var(--bg-muted)'; }}
              onMouseLeave={e => e.currentTarget.style.background='var(--bg-card)'}
              style={{
                background:'var(--bg-card)',
                border:'1px solid var(--border)',
                borderTop:`3px solid ${color}`,
                borderRadius:10,
                padding:'14px 16px',
                display:'flex', alignItems:'center', gap:12,
                cursor: label !== 'Team Members' ? 'pointer' : 'default',
                transition:'background 0.12s',
              }}
            >
              <div style={{ width:38, height:38, borderRadius:9, background:`${color}18`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:17, flexShrink:0 }}>{icon}</div>
              <div>
                <div style={{ fontSize:22, fontWeight:800, color:'var(--text-primary)', lineHeight:1 }}>{value}</div>
                <div style={{ fontSize:10, color:'var(--text-muted)', marginTop:2 }}>{label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Goals + Reviews grid ──────────────────────────── */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, alignItems:'start' }}>

        {/* My Goals */}
        <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:10, overflow:'hidden', height:232, display:'flex', flexDirection:'column' }}>
          <CardHead
            title="My Goals"
            sub={`${goalsList.length} goal${goalsList.length !== 1 ? 's' : ''}${goalsList.filter(g=>g.status==='approved').length > 0 ? ` · ${goalsList.filter(g=>g.status==='approved').length} approved` : ''}`}
            action={<LinkBtn onClick={() => onNavigate('goals')}>Manage →</LinkBtn>}
          />
          {goalsList.length === 0 ? (
            <div style={{ padding:'24px 18px', textAlign:'center', color:'var(--text-muted)' }}>
              <div style={{ fontSize:28, marginBottom:8 }}>🎯</div>
              <div style={{ fontSize:13, fontWeight:600, color:'var(--text-secondary)', marginBottom:4 }}>No goals yet</div>
              <LinkBtn onClick={() => onNavigate('goals')}>Set your first goal →</LinkBtn>
            </div>
          ) : (
            <div style={{ flex:1, overflowY:'auto' }}>
              {goalGroupMeta.map(({ key, label, color }) => {
                const items = goalGroups[key];
                if (!items.length) return null;
                return (
                  <div key={key}>
                    <div style={{ padding:'5px 18px', display:'flex', alignItems:'center', gap:7, background:'var(--bg-muted)', borderTop:'1px solid var(--border)' }}>
                      <span style={{ width:6, height:6, borderRadius:'50%', background:color, display:'inline-block', flexShrink:0 }}/>
                      <span style={{ fontSize:10, fontWeight:700, color, textTransform:'uppercase', letterSpacing:0.7 }}>{label}</span>
                      <span style={{ fontSize:10, color:'var(--text-muted)', marginLeft:'auto' }}>{items.length}</span>
                    </div>
                    {items.map((g, idx) => (
                      <div
                        key={g.id}
                        onClick={() => onNavigate('goals')}
                        style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 18px', cursor:'pointer', borderBottom: idx < items.length - 1 ? '1px solid var(--border)' : 'none', transition:'background 0.12s' }}
                        onMouseEnter={e => e.currentTarget.style.background='var(--bg-muted)'}
                        onMouseLeave={e => e.currentTarget.style.background=''}
                      >
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:13, fontWeight:600, color:'var(--text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{g.goal_name}</div>
                          {g.role_title && <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:1 }}>{g.role_title}</div>}
                        </div>
                        {g.weightage > 0 && (
                          <div style={{ width:52, flexShrink:0 }}>
                            <div style={{ height:3, borderRadius:3, background:'var(--border)' }}>
                              <div style={{ height:'100%', borderRadius:3, width:`${Math.min(100, g.weightage)}%`, background:color }}/>
                            </div>
                            <div style={{ fontSize:10, color:'var(--text-muted)', textAlign:'right', marginTop:2 }}>{g.weightage}%</div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* My Reviews */}
        <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:10, overflow:'hidden', height:232, display:'flex', flexDirection:'column' }}>
          <CardHead
            title="My Reviews"
            sub={`${myReviews.length} review${myReviews.length !== 1 ? 's' : ''} across all cycles`}
            action={myReviews.length > 0 ? <LinkBtn onClick={() => onNavigate('mywork')}>View all →</LinkBtn> : null}
          />
          {myReviews.length === 0 ? (
            <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'24px 18px', textAlign:'center', color:'var(--text-muted)' }}>
              <div style={{ fontSize:28, marginBottom:8 }}>📄</div>
              <div style={{ fontSize:13, fontWeight:600, color:'var(--text-secondary)', marginBottom:2 }}>No reviews yet</div>
              <div style={{ fontSize:11 }}>Reviews appear once a cycle starts</div>
            </div>
          ) : (
            <div style={{ flex:1, overflowY:'auto' }}>
              {myReviews.map((r, idx) => {
                const sc = STATUS_COLOR[r.status] || 'var(--text-muted)';
                const sl = REVIEW_STATUS_LABEL[r.status] || fmtStatus(r.status);
                const score = r.final_score != null ? parseFloat(r.final_score)
                            : r.manager_score != null ? parseFloat(r.manager_score) : null;
                return (
                  <div
                    key={r.id}
                    onClick={() => navigate(`/apps/${appId}/performance/reviews/${r.id}`)}
                    style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 18px', borderBottom: idx < myReviews.length - 1 ? '1px solid var(--border)' : 'none', cursor:'pointer', transition:'background 0.12s' }}
                    onMouseEnter={e => e.currentTarget.style.background='var(--bg-muted)'}
                    onMouseLeave={e => e.currentTarget.style.background=''}
                  >
                    <div style={{ width:36, height:36, borderRadius:8, background:`${sc}14`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      {score != null
                        ? <span style={{ fontSize:13, fontWeight:800, color:sc }}>{score.toFixed(2)}</span>
                        : <span style={{ fontSize:16 }}>📋</span>}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:600, color:'var(--text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {r.cycle_name || `Cycle #${r.cycle_id}`}
                      </div>
                      <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:1 }}>{r.role_title || 'Performance Review'}</div>
                    </div>
                    <StatusPill status={r.status} label={sl}/>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Pending Appraisals ────────────────────────────── */}
      {pendingAppr.length > 0 && (
        <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:10, overflow:'hidden' }}>
          <CardHead
            title="Pending Appraisals"
            sub={`${pendingAppr.length} awaiting action`}
            action={<LinkBtn onClick={() => onNavigate('appraisals')}>View all →</LinkBtn>}
          />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:0 }}>
            {pendingAppr.slice(0, 4).map((a, idx) => {
              const color = STATUS_COLOR[a.status] || 'var(--text-muted)';
              const label = APPR_STATUS_LABEL[a.status] || a.status;
              const emp   = a.employee || a.user;
              return (
                <div
                  key={a.id}
                  onClick={() => onNavigate('appraisals')}
                  style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 18px', borderTop: idx >= 2 ? '1px solid var(--border)' : 'none', borderRight: idx % 2 === 0 ? '1px solid var(--border)' : 'none', cursor:'pointer', transition:'background 0.12s' }}
                  onMouseEnter={e => e.currentTarget.style.background='var(--bg-muted)'}
                  onMouseLeave={e => e.currentTarget.style.background=''}
                >
                  <Avatar user={emp} size={32} color={color}/>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:12, fontWeight:600, color:'var(--text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{uname(emp)}</div>
                    <StatusPill status={a.status} label={label}/>
                  </div>
                </div>
              );
            })}
          </div>
          {pendingAppr.length > 4 && (
            <div style={{ padding:'7px 18px', borderTop:'1px solid var(--border)', textAlign:'center' }}>
              <LinkBtn onClick={() => onNavigate('appraisals')}>+{pendingAppr.length - 4} more →</LinkBtn>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
// ─── Performance Dashboard ────────────────────────────────────────────────────

const APPR_REASON_LABEL = {
  salary_increment:    'Salary Increment',
  annual_appraisal:    'Annual Appraisal',
  promotion:           'Promotion',
  disciplinary_action: 'Disciplinary Action',
  others:              'Others',
};

// ─── Monitoring Dashboard (admin/manager: review workflow + goal status) ──────

// ─── Close / reopen a review or appraisal (admin) ─────────────────────────────
// target: { kind: 'review'|'appraisal', id, name, closed }
function CloseReopenModal({ appId, target, onClose, onDone }) {
  const api = useApi();
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState('');
  const noun = target.kind === 'review' ? 'review' : 'appraisal';

  const submit = async () => {
    if (!target.closed && !reason.trim()) { setErr('Please enter a reason'); return; }
    setSaving(true); setErr('');
    try {
      if (target.kind === 'review') {
        if (target.closed) await api.reopenReview(appId, target.id);
        else               await api.closeReview(appId, target.id, reason.trim());
      } else {
        if (target.closed) await api.reopenAppraisal(appId, target.id);
        else               await api.closeAppraisal(appId, target.id, reason.trim());
      }
      onDone();
    } catch (e) {
      setErr(e.response?.data?.error || e.message || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open size="sm"
      title={target.closed ? `Reopen ${noun}` : `Close ${noun}`}
      onClose={saving ? undefined : onClose}
      footer={
        <div className="form-actions">
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn-primary" onClick={submit} disabled={saving}>
            {saving ? 'Saving…' : target.closed ? 'Reopen' : 'Close'}
          </button>
        </div>
      }
    >
      <div style={{ fontSize: 13.5, lineHeight: 1.6 }}>
        {target.closed ? (
          <p style={{ margin: 0 }}>
            Reopen the {noun} for <strong>{target.name}</strong>? It goes back to the stage it was closed at.
          </p>
        ) : (
          <>
            <p style={{ margin: '0 0 10px' }}>
              Close the {noun} for <strong>{target.name}</strong>? It will be hidden from pending lists. Anything
              already filled in is kept, and you can reopen it from the <strong>Closed</strong> filter.
            </p>
            <label className="form-label">
              <span>Reason <span style={{ color: 'var(--status-danger-fg)' }}>*</span></span>
              <textarea className="form-input" rows={3} maxLength={1000} value={reason}
                onChange={e => setReason(e.target.value)} placeholder="Why is this being closed?" autoFocus />
            </label>
          </>
        )}
        {err && <div className="banner banner-error" style={{ marginTop: 10 }}>{err}</div>}
      </div>
    </Modal>
  );
}

function RowCloseButton({ closed, onClick }) {
  return (
    <button
      onClick={e => { e.stopPropagation(); onClick(); }}
      style={{
        padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
        border: `1px solid ${closed ? 'var(--border)' : 'rgba(255,107,107,0.4)'}`,
        background: 'transparent', color: closed ? 'var(--text-secondary)' : '#ff6b6b',
      }}
    >
      {closed ? 'Reopen' : 'Close'}
    </button>
  );
}

const CLOSED_COLOR = '#8a94a6';

// Reviews and appraisals share one set of status pills (Tasks-style filter).
const UNIFIED_STATUS = [
  { key: 'self',      label: 'Self Pending',     color: '#f0a93b', icon: '👤' },
  { key: 'manager',   label: 'Manager Pending',  color: '#6f7bff', icon: '👥' },
  { key: 'approver',  label: 'Approver Pending', color: '#06b6d4', icon: '⭐' },
  { key: 'completed', label: 'Completed',        color: '#4ad295', icon: '✅' },
  { key: 'rejected',  label: 'Rejected',         color: '#ff6b6b', icon: '✕'  },
  { key: 'closed',    label: 'Closed',           color: CLOSED_COLOR, icon: '🔒' },
];
const UNIFIED_BY_KEY = Object.fromEntries(UNIFIED_STATUS.map(s => [s.key, s]));
// Appraisals tab: only appraisals are listed, so the first stage is the employee's
const APPRAISAL_STATUS_OPTIONS = UNIFIED_STATUS.map(s => (s.key === 'self' ? { ...s, label: 'Employee Pending' } : s));
const REVIEW_TO_UNIFIED = {
  pending_self: 'self', pending_manager: 'manager', pending_approver: 'approver',
  approved: 'completed', rejected: 'rejected', closed: 'closed',
};
const APPR_TO_UNIFIED = {
  pending_employee: 'self', pending_manager: 'manager', pending_approver: 'approver',
  completed: 'completed', rejected: 'rejected', closed: 'closed',
};
const OPEN_UNIFIED = ['self', 'manager', 'approver'];
// No status picked → everything except Closed (Closed shows only when picked)
const statusMatches = (selected, key) => (selected.length ? selected.includes(key) : key !== 'closed');

// Toolbar modelled on the Tasks page: optional type toggles, a Filters panel
// with multi-select pills, and removable chips with Clear all.
function PerfFilterBar({
  types, onToggleType,                       // optional: [{ key, label, on }]
  statuses, setStatuses, statusOptions,
  goalStatuses, setGoalStatuses, goalOptions, // optional
  panelExtra, onClearAll, count,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = React.useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const toggle = (list, setList, key) => setList(list.includes(key) ? list.filter(k => k !== key) : [...list, key]);
  const activeCount = statuses.length + (goalStatuses?.length || 0);

  const chips = [
    ...statuses.map(k => ({ key: `s-${k}`, label: statusOptions.find(o => o.key === k)?.label || k, clear: () => setStatuses(statuses.filter(s => s !== k)) })),
    ...(goalStatuses || []).map(k => ({
      key: `g-${k}`, label: 'Goals: ' + (goalOptions.find(o => o.key === k)?.label || k),
      clear: () => setGoalStatuses(goalStatuses.filter(s => s !== k)),
    })),
  ];

  return (
    <div className="tl-toolbar" style={{ marginBottom: 10 }}>
      {types && (
        <>
          <div className="tl-tf-ownership">
            {types.map(t => (
              <button key={t.key} className={`tl-owner-btn${t.on ? ' active' : ''}`} onClick={() => onToggleType(t.key)}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="tl-toolbar-sep" />
        </>
      )}

      <div className="tl-filter-wrap" ref={wrapRef}>
        <button className={`tl-filter-btn${open ? ' open' : ''}${activeCount > 0 ? ' has-filters' : ''}`} onClick={() => setOpen(o => !o)}>
          Filters
          {activeCount > 0 && <span className="tl-filter-count">{activeCount}</span>}
          <span style={{ fontSize: 9 }}>▾</span>
        </button>
        {open && (
          <div className="tl-filter-panel">
            <div className="tl-fp-grid">
              <div className="tl-fp-field tl-fp-field--full">
                <label className="tl-fp-label">Status</label>
                <div className="tl-fp-pills">
                  {statusOptions.map(s => (
                    <button key={s.key} className={`tl-fp-pill${statuses.includes(s.key) ? ' active' : ''}`}
                      style={{ '--pill-dot': s.color }} onClick={() => toggle(statuses, setStatuses, s.key)}>
                      <span className="tl-fp-dot" />{s.label}
                    </button>
                  ))}
                </div>
              </div>
              {goalOptions && (
                <div className="tl-fp-field tl-fp-field--full">
                  <label className="tl-fp-label">Goal Status</label>
                  <div className="tl-fp-pills">
                    {goalOptions.map(g => (
                      <button key={g.key} className={`tl-fp-pill${goalStatuses.includes(g.key) ? ' active' : ''}`}
                        style={{ '--pill-dot': g.color }} onClick={() => toggle(goalStatuses, setGoalStatuses, g.key)}>
                        <span className="tl-fp-dot" />{g.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {panelExtra}
            </div>
          </div>
        )}
      </div>

      {chips.length > 0 && (
        <>
          <div className="tl-toolbar-sep" />
          <div className="tl-active-chips">
            {chips.map(c => (
              <span key={c.key} className="tl-active-chip" title={c.label}>
                <span className="tl-active-chip-label">{c.label}</span>
                <button className="tl-active-chip-x" onClick={c.clear} title="Remove filter">×</button>
              </span>
            ))}
            <button className="tl-active-chips-reset" onClick={onClearAll}>Clear all</button>
          </div>
        </>
      )}

      <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto' }}>{count} item{count !== 1 ? 's' : ''}</span>
    </div>
  );
}

function MonitoringDashboard({ appId }) {
  const api      = useApi();
  const navigate = useNavigate();

  const [loading,    setLoading]    = useState(true);
  const [cycles,     setCycles]     = useState([]);
  const [wfCycleId,  setWfCycleId]  = useState('');
  const [wfStatus,   setWfStatus]   = useState(null);
  const [wfLoading,  setWfLoading]  = useState(false);
  const [showTypes,    setShowTypes]    = useState({ reviews: true, appraisals: true, goals: false });
  const [statuses,     setStatusesRaw]     = useState(['self']);
  const [goalStatuses, setGoalStatusesRaw] = useState([]);
  const [page,       setPage]       = useState(1);
  const setStatuses     = v => { setStatusesRaw(v);     setPage(1); };
  const setGoalStatuses = v => { setGoalStatusesRaw(v); setPage(1); };
  const [appraisals, setAppraisals] = useState([]);
  const [viewAppraisalId, setViewAppraisalId] = useState(null);
  const [closeTarget, setCloseTarget] = useState(null);
  const [reloadKey,   setReloadKey]   = useState(0);
  const PAGE_SIZE = usePageSize(52, 360);

  const loadAppraisals = useCallback(() => {
    api.listAppraisals(appId, { view: 'all' }).then(d => setAppraisals(d.items || [])).catch(() => {});
  }, [api, appId]);

  useEffect(() => { loadAppraisals(); }, [loadAppraisals]);

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setLoading(true);
    api.listReviewCycles(appId)
      .then(d => {
        const items = d.items || [];
        setCycles(items);
        const active = items.find(c => c.start_date <= today && c.end_date >= today) || items[items.length - 1];
        if (active) setWfCycleId(String(active.id));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [appId]);

  useEffect(() => {
    if (!wfCycleId) return;
    setWfLoading(true);
    setPage(1);
    api.getWorkflowStatus(appId, { cycle_id: wfCycleId })
      .then(d => setWfStatus(d))
      .catch(() => {})
      .finally(() => setWfLoading(false));
  }, [wfCycleId, appId, reloadKey]);

  const today     = new Date().toISOString().split('T')[0];
  const activeCyc = cycles.find(c => c.start_date <= today && c.end_date >= today) || cycles[cycles.length - 1];

  const GOAL_STATUS_META = {
    no_goals:       { color: '#ff6b6b', icon: '⚠️', label: 'No Goals Created' },
    pending:        { color: '#f0a93b', icon: '📝', label: 'Goals Submitted' },
    needs_approval: { color: '#6f7bff', icon: '🔍', label: 'Approval Pending' },
    approved:       { color: '#4ad295', icon: '✅', label: 'Goals Approved' },
  };

  const APPRAISAL_REASON_LABELS = {
    salary_increment:    'Salary Increment',
    annual_appraisal:    'Annual Appraisal',
    promotion:           'Promotion',
    disciplinary_action: 'Disciplinary Action',
    others:               'Other',
  };

  if (loading) return <div className="loading">Loading monitoring…</div>;

  const uname = u => u ? ([u.first_name, u.last_name].filter(Boolean).join(' ') || u.email) : 'Unknown';

  // Every review/appraisal row carries a unified status key (self/manager/…)
  const reviewRows = Object.values(wfStatus?.grouped || {}).flat().map(r => {
    const u = REVIEW_TO_UNIFIED[r.status];
    const s = UNIFIED_BY_KEY[u] || {};
    return {
      _type: 'review',
      id: `wf-${r.id}`,
      employee: r.employee,
      role: r.role_title,
      cycle: u === 'closed'
        ? `Closed: ${r.close_reason || '—'}`
        : (r.cycle_name ? `${r.cycle_name} (${r.year})` : '—'),
      status: u,
      stageColor: s.color,
      stageIcon: s.icon,
      stageLabel: u === 'self' ? 'Self Review Pending' : s.label,
      days: OPEN_UNIFIED.includes(u) ? daysPending(r.created_at) : null,
      onClick: () => navigate(`/apps/${appId}/performance/reviews/${r.id}`),
      action: OPEN_UNIFIED.includes(u) || u === 'closed'
        ? { kind: 'review', id: r.id, name: uname(r.employee), closed: u === 'closed' }
        : null,
    };
  });

  const goalRows = (wfStatus?.goals || []).map(g => {
    const stageKey = g.goal_status || 'no_goals';
    const meta   = GOAL_STATUS_META[stageKey] || GOAL_STATUS_META.no_goals;
    const detail = g.goals_total === 0
      ? 'No goals created'
      : `${g.goals_approved} approved · ${g.goals_submitted} pending · ${g.goals_pending} draft`;
    return {
      _type: 'goal',
      id: `goal-${g.platform_user_id}-${g.role_id || 0}`,
      employee: g.employee,
      role: g.role_title || '—',
      cycle: detail,
      goalStatus: stageKey,
      stageColor: meta.color,
      stageIcon: meta.icon,
      stageLabel: meta.label,
      days: null,
      onClick: null,
    };
  });

  const appraisalRows = appraisals.map(a => {
    const u = APPR_TO_UNIFIED[a.status];
    const s = UNIFIED_BY_KEY[u] || {};
    return {
      _type: 'appraisal',
      id: `ap-${a.id}`,
      employee: a.employee,
      role: a.role?.title || '—',
      cycle: u === 'closed' ? `Closed: ${a.close_reason || '—'}` : (APPRAISAL_REASON_LABELS[a.reason] || a.reason),
      status: u,
      stageColor: s.color,
      stageIcon: s.icon,
      stageLabel: u === 'self' ? 'Employee Pending' : s.label,
      days: OPEN_UNIFIED.includes(u) ? daysPending(a.created_at) : null,
      onClick: () => setViewAppraisalId(a.id),
      action: OPEN_UNIFIED.includes(u) || u === 'closed'
        ? { kind: 'appraisal', id: a.id, name: uname(a.employee), closed: u === 'closed' }
        : null,
    };
  });

  const closedOnly = statuses.length === 1 && statuses[0] === 'closed';
  const filteredRows = [
    ...(showTypes.reviews    ? reviewRows.filter(r => statusMatches(statuses, r.status))    : []),
    ...(showTypes.appraisals ? appraisalRows.filter(r => statusMatches(statuses, r.status)) : []),
    // Goals can't be closed, so a Closed-only filter leaves them out (unless a goal status is picked)
    ...(showTypes.goals && (goalStatuses.length || !closedOnly)
      ? goalRows.filter(r => !goalStatuses.length || goalStatuses.includes(r.goalStatus))
      : []),
  ];

  const TYPE_TOGGLES = [
    { key: 'reviews',    label: '📋 Reviews',    on: showTypes.reviews },
    { key: 'appraisals', label: '🏅 Appraisals', on: showTypes.appraisals },
    { key: 'goals',      label: '🎯 Goals',      on: showTypes.goals },
  ];
  const GOAL_OPTIONS = Object.entries(GOAL_STATUS_META).map(([key, m]) => ({ key, label: m.label, color: m.color }));
  const selectedCyc = cycles.find(c => String(c.id) === String(wfCycleId));

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const pageRows   = filteredRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <>
      {/* Header */}
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>Monitoring</h2>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {selectedCyc || activeCyc
              ? <>Cycle: <strong style={{ color: 'var(--text-primary)' }}>{(selectedCyc || activeCyc).cycle_name} ({(selectedCyc || activeCyc).year})</strong> &nbsp;·&nbsp; {formatDate((selectedCyc || activeCyc).start_date)} → {formatDate((selectedCyc || activeCyc).end_date)}</>
              : 'No active review cycle — create one under Organisation → Review Cycles'}
          </div>
        </div>
      </div>

      {/* Filter + list */}
      <div>
        <PerfFilterBar
          types={TYPE_TOGGLES}
          onToggleType={k => { setShowTypes(t => ({ ...t, [k]: !t[k] })); setPage(1); }}
          statuses={statuses} setStatuses={setStatuses} statusOptions={UNIFIED_STATUS}
          goalStatuses={goalStatuses} setGoalStatuses={setGoalStatuses} goalOptions={GOAL_OPTIONS}
          onClearAll={() => { setShowTypes({ reviews: true, appraisals: true, goals: true }); setStatuses([]); setGoalStatuses([]); }}
          count={filteredRows.length}
          panelExtra={cycles.length > 0 && (
            <div className="tl-fp-field tl-fp-field--full">
              <label className="tl-fp-label">Review Cycle</label>
              <SearchableSelect
                placeholder="Select cycle"
                value={wfCycleId}
                onChange={setWfCycleId}
                btnClass="tl-fp-select"
                showAll={false}
                matchTriggerWidth
                options={cycles.map(c => ({ value: String(c.id), label: `${c.cycle_name} (${c.year})` }))}
              />
            </div>
          )}
        />

        {wfLoading && <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '8px 0' }}>Loading…</div>}

        {!wfLoading && filteredRows.length > 0 && (
          <>
          {totalPages > 1 && (
            <div className="tl-pagination tl-pagination--top">
              <span className="tl-page-info">
                {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filteredRows.length)} of {filteredRows.length} item{filteredRows.length !== 1 ? 's' : ''}
              </span>
              <div className="tl-page-btns">
                <button className="tl-page-btn" disabled={safePage === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(n => n === 1 || n === totalPages || Math.abs(n - safePage) <= 1)
                  .reduce((acc, n, i, arr) => {
                    if (i > 0 && n - arr[i - 1] > 1) acc.push('…');
                    acc.push(n);
                    return acc;
                  }, [])
                  .map((n, i) =>
                    n === '…'
                      ? <span key={`ellipsis-${i}`} className="tl-page-ellipsis">…</span>
                      : <button key={n} className={`tl-page-num${n === safePage ? ' active' : ''}`} onClick={() => setPage(n)}>{n}</button>
                  )
                }
                <button className="tl-page-btn" disabled={safePage >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
              </div>
            </div>
          )}
          <div className="ph-table-scroll" style={{ '--ph-min-w': '640px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10 }}>
            <div style={{
              display: 'grid', gridTemplateColumns: MON_COLS,
              background: 'var(--bg-muted)', borderBottom: '1px solid var(--border)',
              padding: '9px 16px', gap: 10,
            }}>
              {['Employee', 'Role', 'Detail', 'Stage / Status', 'Days', ''].map(h => (
                <div key={h} style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</div>
              ))}
            </div>
            <div style={{ maxHeight:'calc(100vh - 360px)', overflowY:'auto' }}>
              {pageRows.map(r => (
                <div
                  key={r.id}
                  onClick={r.onClick || undefined}
                  style={{
                    display: 'grid', gridTemplateColumns: MON_COLS,
                    padding: '8px 16px', gap: 10, alignItems: 'center',
                    minHeight: 52,
                    borderBottom: '1px solid var(--border)',
                    cursor: r.onClick ? 'pointer' : 'default',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={e => { if (r.onClick) e.currentTarget.style.background = 'var(--bg-card-hover)'; }}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <Avatar user={r.employee} size={28} color={r.stageColor} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflowWrap: 'anywhere', whiteSpace: 'normal' }}>{uname(r.employee)}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.role}</div>
                  <div title={r.cycle} style={{ fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.cycle}</div>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: r.stageColor + '15', color: r.stageColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', maxWidth: '100%', justifySelf: 'start' }}>
                    {r.stageIcon} {r.stageLabel}
                  </span>
                  <DaysBadge days={r.days} />
                  <div>{r.action && <RowCloseButton closed={r.action.closed} onClick={() => setCloseTarget(r.action)} />}</div>
                </div>
              ))}
            </div>
          </div>
          </>
        )}

        {!wfLoading && filteredRows.length === 0 && (
          <EmptyState icon="🎉" title="Nothing to show" subtitle="No items match the selected filter" />
        )}
        {!wfLoading && !wfStatus && cycles.length === 0 && (
          <EmptyState icon="📈" title="No review cycles" subtitle="Create a review cycle under Organisation to get started" />
        )}
      </div>

      {viewAppraisalId && (
        <AppraisalDetail
          appraisalId={viewAppraisalId}
          onClose={() => setViewAppraisalId(null)}
          onRefresh={loadAppraisals}
        />
      )}

      {closeTarget && (
        <CloseReopenModal
          appId={appId}
          target={closeTarget}
          onClose={() => setCloseTarget(null)}
          onDone={() => { setCloseTarget(null); loadAppraisals(); setReloadKey(k => k + 1); }}
        />
      )}
    </>
  );
}

const MON_COLS = 'minmax(160px, 1.6fr) minmax(120px, 1fr) minmax(150px, 1.2fr) minmax(120px, 1fr) 64px 72px';

// ─── Appraisals Tab (admin/manager only) ──────────────────────────────────────

function AppraisalsTab({ appId }) {
  const api          = useApi();
  const { canWrite } = useWorkspace();

  const [loading,        setLoading]        = useState(true);
  const [appraisals,     setAppraisals]     = useState([]);
  const [showCreateAppr, setShowCreateAppr] = useState(false);
  const [statuses,       setStatusesRaw]    = useState(['self']);
  const [page,           setPage]           = useState(1);
  const [closeTarget,    setCloseTarget]    = useState(null);
  const PAGE_SIZE = usePageSize(52, 360);

  const APPR_STATUS_COLORS = {
    pending_employee: '#f0a93b',
    pending_manager:  '#6f7bff',
    pending_approver: '#06b6d4',
    completed:        '#4ad295',
    rejected:         '#ff6b6b',
    closed:           CLOSED_COLOR,
  };
  const APPR_STATUS_LABELS = {
    pending_employee: 'Employee Pending',
    pending_manager:  'Manager Pending',
    pending_approver: 'Approver Pending',
    completed:        'Completed',
    rejected:         'Rejected',
    closed:           'Closed',
  };
  const OPEN_APPR = ['pending_employee', 'pending_manager', 'pending_approver'];

  const load = useCallback(() => {
    setLoading(true);
    Promise.allSettled([
      api.listAppraisals(appId, { view: 'mine' }).catch(() => ({ items: [] })),
      api.listAppraisals(appId, { view: 'manage' }).catch(() => ({ items: [] })),
      api.listAppraisals(appId, { view: 'pending_approver' }).catch(() => ({ items: [] })),
      api.listAppraisals(appId, { view: 'all' }).catch(() => ({ items: [] })),
    ]).then(([mineR, manageR, approverR, allR]) => {
      const seen = new Set();
      const merged = [];
      for (const r of [allR, mineR, manageR, approverR]) {
        for (const a of (r.status === 'fulfilled' ? (r.value?.items || []) : [])) {
          if (!seen.has(a.id)) { seen.add(a.id); merged.push(a); }
        }
      }
      setAppraisals(merged);
    }).finally(() => setLoading(false));
  }, [appId]);

  useEffect(() => { load(); }, [load]);

  const uname = u => u ? ([u.first_name, u.last_name].filter(Boolean).join(' ') || u.email) : 'Unknown';

  const setStatuses = v => { setStatusesRaw(v); setPage(1); };
  // No status picked → everything except Closed
  const filtered = appraisals.filter(a => statusMatches(statuses, APPR_TO_UNIFIED[a.status]));
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const pageRows   = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const statusCounts = appraisals.reduce((acc, a) => { acc[a.status] = (acc[a.status] || 0) + 1; return acc; }, {});

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>Appraisals</h2>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Salary increments, promotions, and other appraisal workflows</div>
        </div>
        {canWrite && (
          <button className="btn-primary" style={{ fontSize: 13 }} onClick={() => setShowCreateAppr(true)}>
            + Trigger Appraisal
          </button>
        )}
      </div>

      {/* Status summary chips */}
      {appraisals.length > 0 && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          {Object.entries(APPR_STATUS_LABELS).map(([key, label]) => {
            const cnt   = statusCounts[key] || 0;
            const color = APPR_STATUS_COLORS[key];
            return (
              <div key={key} onClick={() => setStatuses([APPR_TO_UNIFIED[key]])} style={{
                background: 'var(--bg-card)', border: `1px solid ${color}30`,
                borderLeft: `3px solid ${color}`,
                borderRadius: 8, padding: '8px 14px',
                display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
              }}>
                <span style={{ fontSize: 18, fontWeight: 800, color: cnt > 0 ? color : 'var(--text-muted)' }}>{cnt}</span>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{label}</span>
              </div>
            );
          })}
          <div onClick={() => setStatuses([])} style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderLeft: '3px solid var(--text-muted)',
            borderRadius: 8, padding: '8px 14px',
            display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
          }}>
            <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)' }}>{appraisals.length - (statusCounts.closed || 0)}</span>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>All</span>
          </div>
        </div>
      )}

      <PerfFilterBar
        statuses={statuses} setStatuses={setStatuses} statusOptions={APPRAISAL_STATUS_OPTIONS}
        onClearAll={() => setStatuses([])}
        count={filtered.length}
      />

      {loading && <div className="loading">Loading appraisals…</div>}

      {!loading && filtered.length === 0 && (
        <EmptyState icon="📋" title="No appraisals" subtitle={appraisals.length === 0 ? 'Trigger an appraisal to get started' : 'No appraisals match the selected filters'} />
      )}

      {!loading && filtered.length > 0 && (
        <>
        {totalPages > 1 && (
          <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:10 }}>
            <button onClick={() => setPage(1)} disabled={safePage===1} style={{ padding:'4px 8px', borderRadius:6, border:'1px solid var(--border)', background:'var(--bg-card)', color:'var(--text-primary)', cursor:safePage===1?'default':'pointer', opacity:safePage===1?0.4:1, fontSize:12 }}>«</button>
            <button onClick={() => setPage(p=>Math.max(1,p-1))} disabled={safePage===1} style={{ padding:'4px 8px', borderRadius:6, border:'1px solid var(--border)', background:'var(--bg-card)', color:'var(--text-primary)', cursor:safePage===1?'default':'pointer', opacity:safePage===1?0.4:1, fontSize:12 }}>‹</button>
            <span style={{ fontSize:12, color:'var(--text-muted)', padding:'0 4px' }}>Page {safePage} of {totalPages}</span>
            <button onClick={() => setPage(p=>Math.min(totalPages,p+1))} disabled={safePage===totalPages} style={{ padding:'4px 8px', borderRadius:6, border:'1px solid var(--border)', background:'var(--bg-card)', color:'var(--text-primary)', cursor:safePage===totalPages?'default':'pointer', opacity:safePage===totalPages?0.4:1, fontSize:12 }}>›</button>
            <button onClick={() => setPage(totalPages)} disabled={safePage===totalPages} style={{ padding:'4px 8px', borderRadius:6, border:'1px solid var(--border)', background:'var(--bg-card)', color:'var(--text-primary)', cursor:safePage===totalPages?'default':'pointer', opacity:safePage===totalPages?0.4:1, fontSize:12 }}>»</button>
          </div>
        )}
        <div className="ph-table-scroll" style={{ '--ph-min-w': '640px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10 }}>
          <div style={{
            display: 'grid', gridTemplateColumns: APPR_COLS,
            background: 'var(--bg-muted)', borderBottom: '1px solid var(--border)',
            padding: '9px 16px', gap: 10,
          }}>
            {['Employee', 'Reason', 'Initiated By', 'Status', 'Days', ''].map(h => (
              <div key={h} style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</div>
            ))}
          </div>
          <div style={{ maxHeight:'calc(100vh - 360px)', overflowY:'auto' }}>
            {pageRows.map(a => {
              const color = APPR_STATUS_COLORS[a.status] || 'var(--text-muted)';
              const label = APPR_STATUS_LABELS[a.status] || a.status;
              return (
                <div key={a.id} style={{
                  display: 'grid', gridTemplateColumns: APPR_COLS,
                  padding: '8px 16px', gap: 10, alignItems: 'center',
                  minHeight: 52, borderBottom: '1px solid var(--border)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <Avatar user={a.employee} size={28} color={color} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'normal', wordBreak: 'break-word', minWidth: 0 }}>{uname(a.employee)}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                    {APPR_REASON_LABEL[a.reason] || a.reason}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                    {uname(a.triggered_by)}
                  </div>
                  <span title={a.status === 'closed' ? `Closed: ${a.close_reason || '—'}` : undefined}
                    style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: color + '15', color, whiteSpace: 'nowrap', display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', justifySelf: 'start' }}>
                    {label}
                  </span>
                  <DaysBadge days={OPEN_APPR.includes(a.status) ? daysPending(a.created_at) : null} />
                  <div>
                    {(OPEN_APPR.includes(a.status) || a.status === 'closed') && (
                      <RowCloseButton closed={a.status === 'closed'}
                        onClick={() => setCloseTarget({ kind: 'appraisal', id: a.id, name: uname(a.employee), closed: a.status === 'closed' })} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        </>
      )}

      {showCreateAppr && (
        <CreateAppraisalModal
          onClose={() => setShowCreateAppr(false)}
          onCreated={() => { setShowCreateAppr(false); load(); }}
        />
      )}

      {closeTarget && (
        <CloseReopenModal
          appId={appId}
          target={closeTarget}
          onClose={() => setCloseTarget(null)}
          onDone={() => { setCloseTarget(null); load(); }}
        />
      )}
    </>
  );
}

const APPR_COLS = 'minmax(160px, 1.6fr) minmax(120px, 1fr) minmax(120px, 1fr) minmax(120px, 1fr) 80px 72px';

// ─── My Work (unified pending tasks grid + completed history) ─────────────────

const APPRAISAL_REASON = {
  salary_increment:    'Salary Increment',
  annual_appraisal:    'Annual Appraisal',
  promotion:           'Promotion',
  disciplinary_action: 'Disciplinary Action',
  others:              'Others',
};

function MyWork({ appId, onCountReady }) {
  const api      = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const { searchQuery, organisation, me } = useWorkspace();

  const [myWorkTab,      setMyWorkTab]      = useState('active');  // 'active' | 'completed' | 'rejected' | 'closed'
  const [items,          setItems]          = useState([]);
  const [completedItems, setCompletedItems] = useState([]);
  const [rejectedItems,  setRejectedItems]  = useState([]);
  const [closedItems,    setClosedItems]    = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [filter,         setFilter]         = useState('all');
  const PAGE_SIZE = usePageSize(90, 360);
  const [activePage,    setActivePage]    = useState(1);
  const [completedPage, setCompletedPage] = useState(1);
  const [rejectedPage,  setRejectedPage]  = useState(1);
  const [closedPage,    setClosedPage]    = useState(1);
  const [expandedId,     setExpandedId]     = useState(null);
  const [acting,         setActing]         = useState(false);
  const [rejectModal,    setRejectModal]    = useState(null);
  const [rejectText,     setRejectText]     = useState('');
  const [error,          setError]          = useState('');
  const [appraisalId,    setAppraisalId]    = useState(null); // open AppraisalDetail modal
  const [appraisalViewOnly, setAppraisalViewOnly] = useState(false); // manager+approver same person: viewing their own "sent for approval" entry shouldn't also let them decide from here
  const [viewGoalData,   setViewGoalData]   = useState(null); // goal detail modal
  const [deletingId,     setDeletingId]     = useState(null);
  const [pdfLoading,     setPdfLoading]     = useState(null); // reviewId being downloaded
  const [expandedGoalId, setExpandedGoalId] = useState(null); // which goal row is expanded inside a group

  const apprUserName = u => u
    ? ([u.first_name || u.firstName, u.last_name || u.lastName].filter(Boolean).join(' ') || u.email)
    : '—';

  const downloadReviewPDF = async (item) => {
    if (pdfLoading) return;
    setPdfLoading(item.reviewId);
    try {
      const { jsPDF } = await import('jspdf');
      const { default: autoTable } = await import('jspdf-autotable');

      const [reviewData, ratingsData] = await Promise.all([
        api.getPerformanceReview(appId, item.reviewId),
        api.getReviewRatings(appId, item.reviewId),
      ]);

      const rev          = reviewData.review;
      const employeeName = reviewData.revieweeName || '—';
      const roleName     = rev.role_title || item.detail || '—';
      const cycleName    = rev.cycle_name || item.subject || '—';
      const managerName  = (reviewData.managerNames || []).join(', ') || reviewData.managerName || '—';
      const approverName = (reviewData.finalApproverNames || []).join(', ') || reviewData.finalApproverName || '—';
      const completedOn  = item.completedAt
        ? new Date(item.completedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
        : '—';
      const statusLabel  = item.statusLabel || 'Approved';

      // Snapshot name lookup
      const snapshotMap = {};
      (reviewData.snapshots?.goals  || []).forEach(s => { snapshotMap[`goal-${s.id}`]  = s.goal_name  || s.name || `Goal #${s.id}`; });
      (reviewData.snapshots?.skills || []).forEach(s => { snapshotMap[`skill-${s.id}`] = s.skill_name || s.name || `Skill #${s.id}`; });
      (reviewData.snapshots?.values || []).forEach(s => { snapshotMap[`value-${s.id}`] = s.value_name || s.name || `Value #${s.id}`; });

      // Per-type, per-role averages
      const ratings = ratingsData || [];
      const computeAvg = (type, role) => {
        const f = ratings.filter(r => r.rating_type === type && (!role || r.rater_role === role) && r.rating != null);
        return f.length ? f.reduce((s, r) => s + Number(r.rating), 0) / f.length : null;
      };
      // Goals are weighted by each goal's snapshotted weightage — matches the
      // "Goals (Weighted Avg)" card on the review detail page.
      const goalSnapshots = reviewData.snapshots?.goals || [];
      const goalWeightedAvg = (role) => {
        const totalW = goalSnapshots.reduce((s, g) => s + (g.weightage || 0), 0);
        if (!totalW || !goalSnapshots.length) return null;
        let weighted = 0, hasAny = false;
        for (const g of goalSnapshots) {
          const r = ratings.find(rr => rr.rating_type === 'goal' && rr.snapshot_id === g.id && rr.rater_role === role);
          const rating = r?.rating != null ? Number(r.rating) : 0;
          if (rating > 0) { weighted += (g.weightage || 0) * rating; hasAny = true; }
        }
        return hasAny ? weighted / totalW : null;
      };
      const goalsAvgSelf  = goalWeightedAvg('self');
      const goalsAvgMgr   = goalWeightedAvg('manager');
      const skillsAvgSelf = computeAvg('skill', 'self');
      const skillsAvgMgr  = computeAvg('skill', 'manager');
      const valuesAvgSelf = computeAvg('value', 'self');
      const valuesAvgMgr  = computeAvg('value', 'manager');

      // Overall rating pools every manager rating, but goal ratings are first
      // collapsed to their weighted average so they aren't pooled unweighted
      // (matches the Performance Report's final_score formula).
      const mgrRatings    = ratings.filter(r => r.rater_role === 'manager' && r.rating != null);
      const mgrGoalCount  = mgrRatings.filter(r => r.rating_type === 'goal').length;
      const mgrOtherSum   = mgrRatings.filter(r => r.rating_type !== 'goal').reduce((s, r) => s + Number(r.rating), 0);
      const mgrOtherCount = mgrRatings.length - mgrGoalCount;
      const goalWsum      = goalsAvgMgr != null ? goalsAvgMgr * mgrGoalCount : 0;
      const overallCount  = mgrGoalCount + mgrOtherCount;
      const overallSkill  = overallCount ? (goalWsum + mgrOtherSum) / overallCount : null;
      const overallPct = overallSkill ? Math.round((overallSkill / 5) * 100) : null;

      // Group ratings for table
      const grouped = {};
      ratings.forEach(r => {
        const key  = `${r.rating_type}-${r.snapshot_id}`;
        const name = snapshotMap[key] || r.rating_type || `#${r.snapshot_id}`;
        if (!grouped[key]) grouped[key] = { name, type: r.rating_type, self: null, manager: null, approver: null };
        if (r.rater_role === 'self')           grouped[key].self     = r.rating;
        if (r.rater_role === 'manager')        grouped[key].manager  = r.rating;
        if (r.rater_role === 'final_approver') grouped[key].approver = r.rating;
      });
      const groupedArr = Object.values(grouped);

      // Auto-generate Key Strengths and Areas for Improvement
      const strengths = [], improvements = [];
      groupedArr.forEach(g => {
        const mgr = g.manager != null ? Number(g.manager) : null;
        if (mgr == null) return;
        const label = g.name.split('–')[0].split('-')[0].trim();
        if (mgr >= 4) {
          if (g.type === 'skill') strengths.push(`Strong in ${label}`);
          else if (g.type === 'value') strengths.push(`Demonstrates ${label}`);
          else strengths.push(`Consistent ${label} performance`);
        } else if (mgr < 3.5) {
          if (g.type === 'skill') improvements.push(`Enhance skills in ${label}`);
          else if (g.type === 'goal') improvements.push(`Focus on improving ${label}`);
          else improvements.push(`Strengthen ${label}`);
        }
      });
      if (!strengths.length)    strengths.push('Good overall performance');
      if (!improvements.length) improvements.push('Continue current performance trajectory');

      // Load org logo
      let logoImg = null, logoFmt = 'PNG';
      const apiHost = (process.env.REACT_APP_API_URL || 'http://localhost:8080/api').replace(/\/api$/, '');
      if (organisation?.logo_data) {
        logoImg = organisation.logo_data;
        if (organisation.logo_data.startsWith('data:image/jpeg')) logoFmt = 'JPEG';
      } else if (organisation?.logo_url) {
        try {
          const fullUrl = organisation.logo_url.startsWith('http') ? organisation.logo_url : `${apiHost}${organisation.logo_url}`;
          const resp = await fetch(fullUrl);
          const blob = await resp.blob();
          logoImg = await new Promise(res => { const rd = new FileReader(); rd.onload = e => res(e.target.result); rd.readAsDataURL(blob); });
          if (blob.type?.includes('jpeg') || blob.type?.includes('jpg')) logoFmt = 'JPEG';
        } catch (_) {}
      }
      const orgName = organisation?.name || '';

      // Remove solid background from logo image using canvas pixel manipulation
      const removeBg = (src) => new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = img.width; canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            const id = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const d = id.data;
            const bgR = d[0], bgG = d[1], bgB = d[2];
            const tol = 40;
            for (let i = 0; i < d.length; i += 4) {
              if (Math.abs(d[i]-bgR)<tol && Math.abs(d[i+1]-bgG)<tol && Math.abs(d[i+2]-bgB)<tol)
                d[i+3] = 0;
            }
            ctx.putImageData(id, 0, 0);
            resolve(canvas.toDataURL('image/png'));
          } catch (_) { resolve(src); }
        };
        img.onerror = () => resolve(src);
        img.src = src;
      });
      if (logoImg) { logoImg = await removeBg(logoImg); logoFmt = 'PNG'; }

      // ── Document ──────────────────────────────────────────────────────────────
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const W  = doc.internal.pageSize.getWidth();
      const H  = doc.internal.pageSize.getHeight();
      const ML = 14, MR = 14, CW = W - ML - MR;

      const NAVY    = [0, 31, 63];
      const GREEN   = [22, 163, 74];
      const RED     = [220, 38, 38];
      const BLUE    = [41, 98, 155];
      const GRAY    = [100, 116, 139];
      const DARK    = [30, 41, 59];
      const WHITE   = [255, 255, 255];
      const CYAN_C  = [14, 116, 144];
      const BLUE_C  = [37, 99, 235];
      const EMR_C   = [5, 150, 105];

      // Star helper (filled=true uses fillCol, false=empty grey)
      const drawStar = (cx, cy, outerR, innerR, filled, fillCol) => {
        const pts = [];
        for (let i = 0; i < 10; i++) {
          const angle = (i * Math.PI / 5) - Math.PI / 2;
          const r = (i % 2 === 0) ? outerR : innerR;
          pts.push([cx + r * Math.cos(angle), cy + r * Math.sin(angle)]);
        }
        const rel = pts.slice(1).map((p, i) => [p[0] - pts[i][0], p[1] - pts[i][1]]);
        const col = filled ? (fillCol || NAVY) : [220, 225, 230];
        doc.setFillColor(...col);
        doc.setDrawColor(...col);
        doc.lines(rel, pts[0][0], pts[0][1], [1, 1], 'FD', true);
      };

      // Goal icon: bullseye (3 concentric circles)
      const drawGoalIcon = (cx, cy, r, ic) => {
        doc.setDrawColor(...ic);
        doc.setLineWidth(0.55);
        doc.circle(cx, cy, r * 0.9, 'S');
        doc.circle(cx, cy, r * 0.55, 'S');
        doc.setFillColor(...ic);
        doc.circle(cx, cy, r * 0.22, 'F');
      };

      // Skill icon: 5-pointed star outline
      const drawSkillIcon = (cx, cy, r, ic) => {
        const oR = r * 0.9, iR = r * 0.38;
        const pts2 = [];
        for (let i = 0; i < 10; i++) {
          const angle = (i * Math.PI / 5) - Math.PI / 2;
          const rad = (i % 2 === 0) ? oR : iR;
          pts2.push([cx + rad * Math.cos(angle), cy + rad * Math.sin(angle)]);
        }
        const rel2 = pts2.slice(1).map((p, i) => [p[0] - pts2[i][0], p[1] - pts2[i][1]]);
        doc.setDrawColor(...ic);
        doc.setFillColor(255, 255, 255, 0);
        doc.setLineWidth(0.55);
        doc.lines(rel2, pts2[0][0], pts2[0][1], [1, 1], 'S', true);
      };

      // Value icon: two person silhouettes
      const drawValueIcon = (cx, cy, r, ic) => {
        doc.setFillColor(...ic);
        doc.circle(cx - r * 0.32, cy - r * 0.42, r * 0.28, 'F');
        doc.circle(cx + r * 0.32, cy - r * 0.42, r * 0.28, 'F');
        doc.roundedRect(cx - r * 0.65, cy - r * 0.1, r * 0.6, r * 0.6, 1, 1, 'F');
        doc.roundedRect(cx + r * 0.05, cy - r * 0.1, r * 0.6, r * 0.6, 1, 1, 'F');
      };

      // Info-grid icon helpers (white icons drawn over a filled circle)
      const drawPersonW = (cx, cy, r) => {
        doc.setFillColor(...WHITE);
        doc.circle(cx, cy - r * 0.27, r * 0.26, 'F');
        doc.roundedRect(cx - r * 0.36, cy + r * 0.06, r * 0.72, r * 0.48, r * 0.14, r * 0.14, 'F');
      };
      const drawBriefcaseW = (cx, cy, r) => {
        doc.setFillColor(...WHITE);
        doc.roundedRect(cx - r * 0.48, cy - r * 0.28, r * 0.96, r * 0.62, r * 0.1, r * 0.1, 'F');
        doc.setFillColor(8, 145, 178);
        doc.roundedRect(cx - r * 0.21, cy - r * 0.52, r * 0.42, r * 0.27, r * 0.08, r * 0.08, 'F');
        doc.setFillColor(...WHITE);
        doc.rect(cx - r * 0.48, cy - r * 0.12, r * 0.96, r * 0.06, 'F');
      };
      const drawCalendarW = (cx, cy, r) => {
        doc.setFillColor(...WHITE);
        doc.roundedRect(cx - r * 0.5, cy - r * 0.44, r, r * 0.8, r * 0.1, r * 0.1, 'F');
        doc.setFillColor(8, 145, 178);
        doc.roundedRect(cx - r * 0.5, cy - r * 0.44, r, r * 0.27, r * 0.1, r * 0.1, 'F');
        doc.rect(cx - r * 0.5, cy - r * 0.25, r, r * 0.08, 'F');
        doc.setFillColor(...WHITE);
        [-0.22, 0.08, 0.3].forEach(dx => [0.05, 0.26].forEach(dy => {
          doc.circle(cx + dx * r, cy + dy * r, r * 0.06, 'F');
        }));
      };
      const drawShieldW = (cx, cy, r) => {
        doc.setFillColor(...WHITE);
        doc.roundedRect(cx - r * 0.46, cy - r * 0.52, r * 0.92, r * 0.84, r * 0.22, r * 0.22, 'F');
        doc.setDrawColor(8, 145, 178);
        doc.setLineWidth(0.45);
        doc.lines([[r * 0.32, r * 0.34], [r * 0.52, -r * 0.6]], cx - r * 0.26, cy - r * 0.02, [1, 1], 'S', false);
      };
      const drawCheckW = (cx, cy, r) => {
        doc.setDrawColor(...WHITE);
        doc.setLineWidth(0.5);
        doc.lines([[r * 0.35, r * 0.35], [r * 0.52, -r * 0.62]], cx - r * 0.28, cy - r * 0.02, [1, 1], 'S', false);
      };

      // Footer icon helpers (colored icons on light bg)
      const drawPersonC = (cx, cy, r, col) => {
        doc.setFillColor(...col);
        doc.circle(cx, cy - r * 0.27, r * 0.26, 'F');
        doc.roundedRect(cx - r * 0.36, cy + r * 0.06, r * 0.72, r * 0.48, r * 0.14, r * 0.14, 'F');
      };
      const drawCalendarC = (cx, cy, r, col) => {
        doc.setFillColor(...col);
        doc.roundedRect(cx - r * 0.5, cy - r * 0.44, r, r * 0.8, r * 0.1, r * 0.1, 'F');
        doc.setFillColor(241, 245, 249);
        doc.roundedRect(cx - r * 0.5, cy - r * 0.44, r, r * 0.27, r * 0.1, r * 0.1, 'F');
        doc.rect(cx - r * 0.5, cy - r * 0.25, r, r * 0.08, 'F');
        doc.setFillColor(...col);
        [-0.22, 0.08, 0.3].forEach(dx => [0.05, 0.26].forEach(dy => {
          doc.circle(cx + dx * r, cy + dy * r, r * 0.06, 'F');
        }));
      };
      const drawCheckC = (cx, cy, r, col) => {
        doc.setDrawColor(...col);
        doc.setLineWidth(0.55);
        doc.lines([[r * 0.38, r * 0.36], [r * 0.55, -r * 0.64]], cx - r * 0.3, cy - r * 0.02, [1, 1], 'S', false);
      };

      // White background
      doc.setFillColor(255, 255, 255);
      doc.rect(0, 0, W, H, 'F');

      let y = 8;

      // ── Logo (top-left, aspect-ratio preserved) ───────────────────────────────
      let logoW = 0, logoH = 0;
      if (logoImg) {
        await new Promise(res => {
          const tmp = new Image();
          tmp.onload = () => {
            const maxH = 14;
            const ratio = tmp.naturalWidth / tmp.naturalHeight;
            logoH = maxH;
            logoW = Math.min(maxH * ratio, 45);
            res();
          };
          tmp.onerror = res;
          tmp.src = logoImg;
        });
        try { doc.addImage(logoImg, logoFmt, ML, y, logoW, logoH); } catch (_) {}
      }

      // ── Company name: centered, above the main title ──────────────────────────
      let titleY = y + (logoImg ? logoH - 1 : 2);
      if (orgName) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(...NAVY);
        doc.text(orgName.toUpperCase(), W / 2, titleY, { align: 'center' });
        titleY += 10;
      } else {
        titleY += 3;
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(...NAVY);
      doc.text('EMPLOYEE REVIEW REPORT', W / 2, titleY, { align: 'center' });
      y = titleY + 5;

      // Badge with lines
      const badgeText = cycleName.toUpperCase();
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      const badgeW  = doc.getTextWidth(badgeText) + 14;
      const badgeCX = W / 2;
      const badgeX  = badgeCX - badgeW / 2;
      const badgeMY = y + 3.5;
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.4);
      doc.line(ML, badgeMY, badgeX - 4, badgeMY);
      doc.line(badgeX + badgeW + 4, badgeMY, W - MR, badgeMY);
      doc.setFillColor(29, 56, 84);
      doc.roundedRect(badgeX, y, badgeW, 7, 2, 2, 'F');
      doc.setTextColor(...WHITE);
      doc.text(badgeText, badgeCX, y + 5, { align: 'center' });
      y += 11;

      // Thin border under header
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.line(ML, y, W - MR, y);
      y += 3;

      // ── Stats Row (3 cards) ───────────────────────────────────────────────────
      const statW = (CW - 8) / 3;
      const statH = 19;
      const statsConfig = [
        { label: 'GOALS',  icon: 'goal',  self: goalsAvgSelf,  mgr: goalsAvgMgr,  bg: [207, 250, 254], ic: CYAN_C },
        { label: 'SKILLS', icon: 'skill', self: skillsAvgSelf, mgr: skillsAvgMgr, bg: [219, 234, 254], ic: BLUE_C },
        { label: 'VALUES', icon: 'value', self: valuesAvgSelf, mgr: valuesAvgMgr, bg: [209, 250, 229], ic: EMR_C  },
      ];

      statsConfig.forEach((sc, i) => {
        const cx = ML + i * (statW + 4);
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(cx, y, statW, statH, 3, 3, 'F');
        doc.setDrawColor(226, 232, 240);
        doc.setLineWidth(0.3);
        doc.roundedRect(cx, y, statW, statH, 3, 3, 'S');

        const iconR  = 5.5;
        const iconCX = cx + iconR + 3;
        const iconCY = y + statH / 2;
        doc.setFillColor(...sc.bg);
        doc.circle(iconCX, iconCY, iconR, 'F');
        if (sc.icon === 'goal')  drawGoalIcon(iconCX, iconCY, iconR * 0.7, sc.ic);
        if (sc.icon === 'skill') drawSkillIcon(iconCX, iconCY, iconR * 0.75, sc.ic);
        if (sc.icon === 'value') drawValueIcon(iconCX, iconCY, iconR * 0.9, sc.ic);

        const tx = iconCX + iconR + 3;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(6);
        doc.setTextColor(...GRAY);
        doc.text(sc.label, tx, y + 5.5);

        const selfStr = sc.self != null ? sc.self.toFixed(2) : '—';
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(...sc.ic);
        const numW = doc.getTextWidth(selfStr);
        doc.text(selfStr, tx, y + 13);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.5);
        doc.setTextColor(...GRAY);
        doc.text('/5', tx + numW + 1, y + 13);

        if (sc.mgr != null) {
          const mgrStr = `${sc.mgr.toFixed(2)} mgr`;
          const mgrX   = tx + numW + 6;
          const mgrW   = doc.getTextWidth(mgrStr) + 4;
          doc.setFillColor(239, 246, 255);
          doc.setDrawColor(191, 219, 254);
          doc.setLineWidth(0.2);
          doc.roundedRect(mgrX, y + 9.5, mgrW, 4.5, 1, 1, 'FD');
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(5.5);
          doc.setTextColor(37, 99, 235);
          doc.text(mgrStr, mgrX + 2, y + 13);
        }
      });
      y += statH + 3;

      // ── Info Grid + Rating Card ───────────────────────────────────────────────
      const infoW  = CW * 0.62;
      const rateW  = CW - infoW - 5;
      const panelH = 42;

      // Info card
      doc.setFillColor(252, 252, 253);
      doc.roundedRect(ML, y, infoW, panelH, 3, 3, 'F');
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.roundedRect(ML, y, infoW, panelH, 3, 3, 'S');

      // Grid dividers
      const colW = infoW / 2;
      const cellH = panelH / 3;
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.2);
      doc.line(ML + colW, y, ML + colW, y + panelH);
      doc.line(ML, y + cellH,     ML + infoW, y + cellH);
      doc.line(ML, y + cellH * 2, ML + infoW, y + cellH * 2);

      const infoFields = [
        ['EMPLOYEE',       employeeName, DARK],
        ['FINAL APPROVER', approverName, DARK],
        ['ROLE',           roleName,     DARK],
        ['COMPLETED ON',   completedOn,  DARK],
        ['MANAGER',        managerName,  DARK],
        ['STATUS',         statusLabel,  statusLabel === 'Approved' ? GREEN : statusLabel === 'Rejected' ? RED : BLUE],
      ];

      infoFields.forEach(([lbl, val, valColor], idx) => {
        const colIdx = idx % 2;
        const rowIdx = Math.floor(idx / 2);
        const cx2 = ML + colIdx * colW + 4;
        const cy2 = y + rowIdx * cellH + 5;

        const icx = cx2 + 2.5, icy = cy2 + 2.5;
        doc.setFillColor(...NAVY);
        doc.circle(icx, icy, 2.5, 'F');
        if (lbl === 'EMPLOYEE' || lbl === 'MANAGER' || lbl === 'FINAL APPROVER') drawPersonW(icx, icy, 2.5);
        else if (lbl === 'ROLE') drawBriefcaseW(icx, icy, 2.5);
        else if (lbl === 'COMPLETED ON') drawCalendarW(icx, icy, 2.5);
        else if (lbl === 'STATUS') drawShieldW(icx, icy, 2.5);

        const tx2 = cx2 + 7;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(5);
        doc.setTextColor(...GRAY);
        doc.text(lbl, tx2, cy2 + 1);

        // Shrink to fit on one line instead of splitTextToSize(...)[0],
        // which silently drops everything past the first wrapped line
        // (e.g. "Assistant Manager - Software Testing" losing "Testing").
        doc.setFont('helvetica', 'bold');
        const valStr  = String(val);
        const maxValW = colW - 13;
        let valSize   = 7.5;
        doc.setFontSize(valSize);
        while (doc.getTextWidth(valStr) > maxValW && valSize > 5) {
          valSize -= 0.25;
          doc.setFontSize(valSize);
        }
        doc.setTextColor(...valColor);
        doc.text(valStr, tx2, cy2 + 6);
      });

      // Rating card
      const rcx = ML + infoW + 5;
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(rcx, y, rateW, panelH, 3, 3, 'F');
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.roundedRect(rcx, y, rateW, panelH, 3, 3, 'S');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.5);
      doc.setTextColor(...GRAY);
      doc.text('OVERALL RATING', rcx + rateW / 2, y + 5.5, { align: 'center' });

      if (overallSkill != null) {
        const numStr  = overallSkill.toFixed(2);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(18);
        doc.setTextColor(...NAVY);
        const numStrW  = doc.getTextWidth(numStr);
        const numStart = rcx + rateW / 2 - (numStrW + 9) / 2;
        doc.text(numStr, numStart, y + 16);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...GRAY);
        doc.text('/ 5.00', numStart + numStrW + 2, y + 16);

        const starOuterR = 2.5, starInnerR = 1.1, starSpacing = 7;
        const starsW = 4 * starSpacing + starOuterR * 2;
        const starSX = rcx + rateW / 2 - starsW / 2 + starOuterR;
        const starY  = y + 23;
        const filled = Math.round(overallSkill);
        for (let s = 0; s < 5; s++) drawStar(starSX + s * starSpacing, starY, starOuterR, starInnerR, s < filled);

        doc.setDrawColor(226, 232, 240);
        doc.setLineWidth(0.2);
        doc.line(rcx + 4, y + 28, rcx + rateW - 4, y + 28);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.setTextColor(...NAVY);
        doc.text(`${overallPct}%`, rcx + rateW / 2, y + 36, { align: 'center' });
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(5);
        doc.setTextColor(...GRAY);
        doc.text('OVERALL PERFORMANCE', rcx + rateW / 2, y + 40.5, { align: 'center' });
      }
      y += panelH + 4;

      // ── Ratings Table ─────────────────────────────────────────────────────────
      if (groupedArr.length) {
        autoTable(doc, {
          startY: y,
          head: [['SI NO', 'ITEM', 'TYPE', 'SELF (5)', 'MANAGER (5)']],
          body: groupedArr.map((g, idx) => [
            idx + 1,
            g.name,
            g.type ? (g.type.charAt(0).toUpperCase() + g.type.slice(1)) : '—',
            g.self    != null ? g.self    : '–',
            g.manager != null ? g.manager : '–',
          ]),
          margin: { left: ML, right: MR },
          tableWidth: CW,
          columnStyles: {
            0: { cellWidth: 9,   halign: 'center' },
            1: { cellWidth: 105, halign: 'center' },
            2: { cellWidth: 20,  halign: 'center' },
            3: { cellWidth: 24,  halign: 'center' },
            4: { cellWidth: 24,  halign: 'center' },
          },
          styles: { fontSize: 7.5, cellPadding: 2, textColor: [...DARK], lineColor: [226, 232, 240], lineWidth: 0.2 },
          headStyles: { fillColor: [29, 56, 84], textColor: [...WHITE], fontStyle: 'bold', fontSize: 7, halign: 'center', cellPadding: 2.5 },
          alternateRowStyles: { fillColor: [252, 252, 252] },
          rowPageBreak: 'avoid',
        });
        y = doc.lastAutoTable.finalY + 4;
      }

      // ── Key Strengths + Areas for Improvement ─────────────────────────────────
      y += 4;
      const strSlice = strengths.slice(0, 3);
      const impSlice = improvements.slice(0, 3);
      const maxItems = Math.max(strSlice.length, impSlice.length);
      const ftrY = H - 20;
      const availH = ftrY - y - 5;
      const itemH  = Math.min(10, Math.floor((availH - 13) / Math.max(maxItems, 1)));
      const boxH   = maxItems * itemH + 13;
      const boxW   = (CW - 5) / 2;

      // Key Strengths box
      doc.setFillColor(240, 253, 244);
      doc.roundedRect(ML, y, boxW, boxH, 3, 3, 'F');
      doc.setDrawColor(187, 247, 208);
      doc.setLineWidth(0.25);
      doc.roundedRect(ML, y, boxW, boxH, 3, 3, 'S');
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(ML, y, boxW, 9, 3, 3, 'F');
      doc.rect(ML, y + 5, boxW, 4, 'F');
      doc.setDrawColor(187, 247, 208);
      doc.line(ML, y + 9, ML + boxW, y + 9);
      doc.setFillColor(...GREEN);
      doc.circle(ML + 6, y + 4.5, 3, 'F');
      // White checkmark inside green circle
      doc.setDrawColor(...WHITE);
      doc.setLineWidth(0.65);
      doc.lines([[0.9, 0.95], [1.7, -1.9]], ML + 4.4, y + 4.7, [1, 1], 'S', false);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(22, 101, 52);
      doc.text('KEY STRENGTHS', ML + 11, y + 5.5, { baseline: 'middle' });

      strSlice.forEach((s, i) => {
        const iy = y + 12 + i * itemH;
        doc.setFillColor(...GREEN);
        doc.circle(ML + 6, iy, 2, 'F');
        // White checkmark inside green dot
        doc.setDrawColor(...WHITE);
        doc.setLineWidth(0.45);
        doc.lines([[0.65, 0.65], [1.1, -1.15]], ML + 4.85, iy + 0.1, [1, 1], 'S', false);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.setTextColor(55, 65, 81);
        doc.text(doc.splitTextToSize(s, boxW - 17)[0], ML + 10, iy + 1.5);
      });

      // Areas for Improvement box
      const rx = ML + boxW + 5;
      doc.setFillColor(255, 241, 242);
      doc.roundedRect(rx, y, boxW, boxH, 3, 3, 'F');
      doc.setDrawColor(254, 202, 202);
      doc.setLineWidth(0.25);
      doc.roundedRect(rx, y, boxW, boxH, 3, 3, 'S');
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(rx, y, boxW, 9, 3, 3, 'F');
      doc.rect(rx, y + 5, boxW, 4, 'F');
      doc.setDrawColor(254, 202, 202);
      doc.line(rx, y + 9, rx + boxW, y + 9);
      doc.setFillColor(...RED);
      doc.circle(rx + 6, y + 4.5, 3, 'F');
      // White minus icon inside red circle
      doc.setDrawColor(...WHITE);
      doc.setLineWidth(0.9);
      doc.line(rx + 3.8, y + 4.5, rx + 8.2, y + 4.5);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(153, 27, 27);
      doc.text('AREAS FOR IMPROVEMENT', rx + 11, y + 5.5, { baseline: 'middle' });

      impSlice.forEach((imp, i) => {
        const iy = y + 12 + i * itemH;
        doc.setFillColor(...RED);
        doc.circle(rx + 6, iy, 2, 'F');
        // White dash inside red dot
        doc.setDrawColor(...WHITE);
        doc.setLineWidth(0.55);
        doc.line(rx + 4.7, iy, rx + 7.3, iy);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.setTextColor(55, 65, 81);
        doc.text(doc.splitTextToSize(imp, boxW - 17)[0], rx + 10, iy + 1.5);
      });

      // ── Footer ────────────────────────────────────────────────────────────────
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.line(ML, ftrY, W - MR, ftrY);

      const ftrItems = [
        { label: 'MANAGER',      value: managerName,  color: DARK,  iconBg: [241, 245, 249], iconC: GRAY },
        { label: 'STATUS',       value: statusLabel,   color: statusLabel === 'Approved' ? GREEN : statusLabel === 'Rejected' ? RED : BLUE, iconBg: [240, 253, 244], iconC: GREEN },
        { label: 'GENERATED ON', value: new Date().toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }), color: DARK, iconBg: [241, 245, 249], iconC: GRAY },
      ];

      ftrItems.forEach(({ label, value, color, iconBg, iconC }, i) => {
        const fx = ML + i * (CW / 3) + 3;
        const fy = ftrY + 3;
        const ficx = fx + 3.5, ficy = fy + 3.5;
        doc.setFillColor(...iconBg);
        doc.circle(ficx, ficy, 3.5, 'F');
        if (label === 'MANAGER') drawPersonC(ficx, ficy, 3.5, iconC);
        else if (label === 'STATUS') drawCheckC(ficx, ficy, 3.5, iconC);
        else if (label === 'GENERATED ON') drawCalendarC(ficx, ficy, 3.5, iconC);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(5.5);
        doc.setTextColor(...GRAY);
        doc.text(label, fx + 9, fy + 1.5);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.setTextColor(...color);
        doc.text(String(value), fx + 9, fy + 7);
        doc.setDrawColor(226, 232, 240);
        doc.setLineWidth(0.2);
        doc.line(fx + 9, fy + 9, fx + CW / 3 - 5, fy + 9);
      });

      doc.setFont('helvetica', 'italic');
      doc.setFontSize(7);
      doc.setTextColor(...GRAY);
      doc.text('This is a system generated report.', W / 2, H - 5, { align: 'center' });

      const filename = `review-${employeeName.replace(/\s+/g, '-')}-${cycleName.replace(/\s+/g, '-')}.pdf`;
      doc.save(filename);
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setPdfLoading(null);
    }
  };

  const load = useCallback(() => {
    setLoading(true);
    Promise.allSettled([
      api.listMyReviews(appId),
      api.listTeamGoals(appId),
      api.listTeamReviews(appId),
      api.listPendingForMe(appId),
      api.listAppraisals(appId, { view: 'mine' }),
      api.listAppraisals(appId, { view: 'manage' }),
      api.listAppraisals(appId, { view: 'pending_approver' }),
    ]).then(([reviewsR, goalsR, teamRevR, finalR, apprMineR, apprManageR, apprApproverR]) => {
      const active    = [];
      const completed = [];
      const closed    = [];

      // ── Active items ──────────────────────────────────────────────────────

      // My self-ratings pending
      if (reviewsR.status === 'fulfilled') {
        (reviewsR.value.items || []).forEach(r => {
          if (r.status === 'pending_self') {
            const isReturned = r.rejection_target === 'self';
            active.push({
              id:              `review-${r.id}`,
              type:            'self-rating',
              typeLabel:       'Self Rating',
              typeColor:       '#f0a93b',
              subject:         r.cycle_name || (r.cycle_id ? `Cycle #${r.cycle_id}` : 'No Cycle'),
              detail:          r.role_title  || `Role #${r.role_id}`,
              userName:        apprUserName(me),
              status:          r.status,
              statusLabel:     isReturned ? 'Returned for Revision' : 'Pending Self Rating',
              isReturned,
              rejectionReason: r.rejection_reason || null,
              createdAt:       r.created_at,
              reviewId:        r.id,
              navigateTo:      `/apps/${appId}/performance/reviews/${r.id}`,
              action:          'Rate Myself',
            });
          } else if (r.status === 'pending_manager') {
            active.push({
              id:         `review-${r.id}`,
              type:       'my-review',
              typeLabel:  'My Review',
              typeColor:  '#6f7bff',
              subject:    r.cycle_name || (r.cycle_id ? `Cycle #${r.cycle_id}` : 'No Cycle'),
              detail:     r.role_title  || `Role #${r.role_id}`,
              userName:   apprUserName(me),
              status:     r.status,
              statusLabel:'With Manager',
              createdAt:  r.created_at,
              reviewId:   r.id,
              navigateTo: `/apps/${appId}/performance/reviews/${r.id}`,
              action:     'View',
            });
          } else if (r.status === 'pending_approver') {
            active.push({
              id:         `review-${r.id}`,
              type:       'my-review',
              typeLabel:  'My Review',
              typeColor:  '#06b6d4',
              subject:    r.cycle_name || (r.cycle_id ? `Cycle #${r.cycle_id}` : 'No Cycle'),
              detail:     r.role_title  || `Role #${r.role_id}`,
              userName:   apprUserName(me),
              status:     r.status,
              statusLabel:'With Final Approver',
              createdAt:  r.created_at,
              reviewId:   r.id,
              navigateTo: `/apps/${appId}/performance/reviews/${r.id}`,
              action:     'View',
            });
          } else if (['approved', 'rejected'].includes(r.status)) {
            // Completed self-reviews
            completed.push({
              id:          `done-review-${r.id}`,
              type:        'my-review',
              typeLabel:   'My Review',
              typeColor:   r.status === 'approved' ? '#4ad295' : '#ff6b6b',
              subject:     r.cycle_name || (r.cycle_id ? `Cycle #${r.cycle_id}` : 'No Cycle'),
              detail:      r.role_title  || `Role #${r.role_id}`,
              userName:    apprUserName(me),
              status:      r.status,
              statusLabel: r.status === 'approved' ? 'Approved' : 'Rejected',
              completedAt: r.final_approved_at || r.manager_reviewed_at || r.self_submitted_at || r.created_at,
              reviewId:    r.id,
              navigateTo:  `/apps/${appId}/performance/reviews/${r.id}`,
            });
          } else if (r.status === 'closed') {
            closed.push({
              id:          `closed-review-${r.id}`,
              type:        'my-review',
              typeLabel:   'My Review',
              typeColor:   '#8a94a6',
              subject:     r.cycle_name || (r.cycle_id ? `Cycle #${r.cycle_id}` : 'No Cycle'),
              detail:      r.role_title  || `Role #${r.role_id}`,
              userName:    apprUserName(me),
              status:      r.status,
              statusLabel: 'Closed',
              completedAt: r.closed_at || r.updated_at || r.created_at,
              reviewId:    r.id,
              navigateTo:  `/apps/${appId}/performance/reviews/${r.id}`,
            });
          }
        });
      }

      // Team goal approvals pending — grouped by user+cycle+role
      if (goalsR.status === 'fulfilled') {
        (goalsR.value.groups || []).forEach(grp => {
          const uName = [grp.user?.first_name, grp.user?.last_name].filter(Boolean).join(' ') || grp.user?.email || 'User';
          active.push({
            id:         `goal-group-${grp.key}`,
            type:       'goal-approval',
            typeLabel:  'Goal Approval',
            typeColor:  '#ff6b6b',
            subject:    grp.cycle_name || (grp.cycle_id ? `Cycle #${grp.cycle_id}` : 'No Cycle'),
            detail:     grp.role_title || '—',
            userName:   uName,
            status:     'submitted',
            statusLabel:'Awaiting Approval',
            createdAt:  grp.goals[0]?.created_at,
            goalGroup:  grp,
            action:     'Review',
          });
        });
      }

      // Team reviews
      const rejected = [];
      if (teamRevR.status === 'fulfilled') {
        (teamRevR.value.items || []).forEach(r => {
          const subject  = r.cycle_name || (r.cycle_id ? `Cycle #${r.cycle_id}` : 'No Cycle');
          const detail   = r.role_title || '—';
          const uNameRow = userName(r.user);
          if (r.status === 'pending_manager') {
            const isReturned = r.rejection_target === 'manager';
            active.push({
              id:              `team-${r.id}`,
              type:            'team-review',
              typeLabel:       'Team Review',
              typeColor:       '#6f7bff',
              subject,
              detail,
              userName:        uNameRow,
              status:          r.status,
              statusLabel:     isReturned ? 'Returned by Final Approver' : 'Manager Rating Pending',
              isReturned,
              rejectionReason: r.rejection_reason || null,
              createdAt:       r.created_at,
              reviewId:        r.id,
              navigateTo:      `/apps/${appId}/performance/reviews/${r.id}`,
              action:          'Rate Now',
            });
          } else if (r.status === 'pending_approver') {
            completed.push({
              id:          `team-final-${r.id}`,
              type:        'team-review',
              typeLabel:   'Team Review',
              typeColor:   '#06b6d4',
              subject,
              detail,
              userName:    uNameRow,
              status:      r.status,
              statusLabel: 'Sent for Final Approval',
              completedAt: r.manager_reviewed_at || r.updated_at || r.created_at,
              reviewId:    r.id,
              navigateTo:  `/apps/${appId}/performance/reviews/${r.id}`,
            });
          } else if (r.status === 'approved') {
            completed.push({
              id:          `done-team-${r.id}`,
              type:        'team-review',
              typeLabel:   'Team Review',
              typeColor:   '#4ad295',
              subject,
              detail,
              userName:    uNameRow,
              status:      r.status,
              statusLabel: 'Approved',
              completedAt: r.final_approved_at || r.updated_at || r.created_at,
              reviewId:    r.id,
              navigateTo:  `/apps/${appId}/performance/reviews/${r.id}`,
            });
          } else if (r.status === 'rejected') {
            rejected.push({
              id:          `rej-team-${r.id}`,
              type:        'team-review',
              typeLabel:   'Team Review',
              typeColor:   '#ff6b6b',
              subject,
              detail,
              userName:    uNameRow,
              status:      r.status,
              statusLabel: 'Rejected',
              rejectionReason: r.rejection_reason || null,
              completedAt: r.updated_at || r.created_at,
              reviewId:    r.id,
              navigateTo:  `/apps/${appId}/performance/reviews/${r.id}`,
            });
          } else if (r.status === 'pending_self' && r.rejection_target === 'self') {
            // Manager returned review back to employee
            rejected.push({
              id:          `ret-team-${r.id}`,
              type:        'team-review',
              typeLabel:   'Team Review',
              typeColor:   '#f59e0b',
              subject,
              detail,
              userName:    uNameRow,
              status:      r.status,
              statusLabel: 'Returned to Employee',
              rejectionReason: r.rejection_reason || null,
              completedAt: r.updated_at || r.created_at,
              reviewId:    r.id,
              navigateTo:  `/apps/${appId}/performance/reviews/${r.id}`,
            });
          } else if (r.status === 'closed') {
            closed.push({
              id:          `closed-team-${r.id}`,
              type:        'team-review',
              typeLabel:   'Team Review',
              typeColor:   '#8a94a6',
              subject,
              detail,
              userName:    uNameRow,
              status:      r.status,
              statusLabel: 'Closed',
              completedAt: r.closed_at || r.updated_at || r.created_at,
              reviewId:    r.id,
              navigateTo:  `/apps/${appId}/performance/reviews/${r.id}`,
            });
          }
        });
      }

      // Final approvals — pending (active), approved (completed), rejected (rejected)
      if (finalR.status === 'fulfilled') {
        (finalR.value.items || []).forEach(r => {
          const baseFA = {
            type:       'final-approval',
            typeLabel:  'Final Approval',
            typeColor:  '#06b6d4',
            subject:    r.cycle_name || (r.cycle_id ? `Cycle #${r.cycle_id}` : 'No Cycle'),
            detail:     r.role_title || '—',
            userName:   userName(r.user),
            status:     r.status,
            reviewId:   r.id,
            navigateTo: `/apps/${appId}/performance/reviews/${r.id}`,
          };
          if (r.status === 'pending_approver') {
            active.push({ ...baseFA, id: `final-${r.id}`, statusLabel: 'Awaiting Sign-off', createdAt: r.created_at, action: 'Approve' });
          } else if (r.status === 'approved') {
            completed.push({ ...baseFA, id: `fa-done-${r.id}`, statusLabel: 'Approved', completedAt: r.final_approved_at || r.updated_at });
          } else if (r.status === 'rejected') {
            rejected.push({ ...baseFA, id: `fa-rej-${r.id}`, statusLabel: 'Rejected', rejectionReason: r.rejection_reason || null, completedAt: r.final_approved_at || r.updated_at });
          }
        });
      }

      // ── Appraisals ────────────────────────────────────────────────────────
      // Rejected appraisals must land in the dedicated Rejected tab, not get
      // lumped into Completed with a "Rejected" label — each of the three
      // sides (self, manager, final approver) needs its own outcome to show
      // up on its own side, individually, not just whichever one currently
      // works.

      // Mine (as employee)
      if (apprMineR.status === 'fulfilled') {
        (apprMineR.value.items || []).forEach(a => {
          const reasonLabel = APPRAISAL_REASON[a.reason] || a.reason;
          const cycles = (a.included_cycles || []).map(c => c.cycle_name).join(', ') || '—';
          const base = {
            id:         `appr-mine-${a.id}`,
            type:       'appraisal',
            typeLabel:  'Appraisal',
            typeColor:  '#ff6b6b',
            subject:    reasonLabel,
            detail:     cycles,
            userName:   apprUserName(me),
            appraisalId: a.id,
            action:     'Open',
          };
          if (a.status === 'pending_employee') {
            const isReturned = !!a.rejected_by_stage;
            active.push({
              ...base, status: a.status,
              statusLabel: isReturned ? 'Returned for Revision' : 'My Reflection Pending',
              isReturned,
              rejectionReason: a.rejection_reason || null,
              createdAt: a.created_at,
            });
          } else if (a.status === 'pending_manager') {
            // My part is done — waiting on the manager. Without this branch
            // the appraisal vanishes from every one of my own tabs the
            // moment I submit, until it eventually completes or bounces back.
            active.push({ ...base, status: a.status, statusLabel: 'With Manager', createdAt: a.created_at, action: 'View' });
          } else if (a.status === 'pending_approver') {
            active.push({ ...base, status: a.status, statusLabel: 'With Final Approver', createdAt: a.created_at, action: 'View' });
          } else if (a.status === 'completed') {
            completed.push({ ...base, status: a.status, statusLabel: 'Completed', completedAt: a.final_decided_at || a.updated_at || a.created_at });
          } else if (a.status === 'rejected') {
            rejected.push({ ...base, status: a.status, statusLabel: 'Rejected', completedAt: a.final_decided_at || a.updated_at || a.created_at });
          } else if (a.status === 'closed') {
            closed.push({ ...base, status: a.status, statusLabel: 'Closed', completedAt: a.closed_at || a.updated_at || a.created_at });
          }
        });
      }

      // Manage (as manager)
      if (apprManageR.status === 'fulfilled') {
        (apprManageR.value.items || []).forEach(a => {
          const reasonLabel = APPRAISAL_REASON[a.reason] || a.reason;
          const cycles = (a.included_cycles || []).map(c => c.cycle_name).join(', ') || '—';
          const base = {
            id:         `appr-manage-${a.id}`,
            type:       'appraisal',
            typeLabel:  'Appraisal (Manager)',
            typeColor:  '#ff6b6b',
            subject:    reasonLabel,
            detail:     cycles,
            userName:   apprUserName(a.employee),
            appraisalId: a.id,
            action:     'Review',
          };
          if (a.status === 'pending_manager') {
            // rejected_by_stage='final_approver' here means the final approver
            // sent it back for re-review; 'manager' means the employee just
            // resubmitted after this manager's own earlier rejection — both
            // need to read differently from a fresh, first-time submission.
            const isReturned = a.rejected_by_stage === 'final_approver';
            const isResubmitted = a.rejected_by_stage === 'manager';
            active.push({
              ...base, status: a.status,
              statusLabel: isReturned ? 'Returned by Final Approver' : isResubmitted ? 'Employee Resubmitted' : 'Manager Review Pending',
              isReturned,
              rejectionReason: a.rejection_reason || null,
              createdAt: a.created_at,
            });
          } else if (a.status === 'pending_employee' && a.rejected_by_stage) {
            // Sent back to the employee — either this manager rejected it
            // themselves, or the final approver bounced it past the manager's
            // own earlier approval. Either way it must still show up on the
            // manager's own side instead of vanishing until the employee
            // resubmits.
            rejected.push({
              ...base, id: `appr-manage-rej-${a.id}`, status: a.status, statusLabel: 'Returned to Employee',
              rejectionReason: a.rejection_reason || null,
              completedAt: a.manager_submitted_at || a.updated_at || a.created_at,
            });
          } else if (a.status === 'pending_approver') {
            // My review is done and it's moved on — without this branch it
            // vanishes from my tabs the moment I approve, same gap as above.
            // When I'm also the final approver, this same appraisal already
            // has an "Awaiting Final Decision" entry in Active Work — mark
            // this one viewOnly so opening it here can't also be used to
            // make the final decision.
            completed.push({ ...base, status: a.status, statusLabel: 'Sent for Final Approval', completedAt: a.manager_submitted_at || a.updated_at || a.created_at, viewOnly: !!a.is_final_approver });
          } else if (a.status === 'completed') {
            // Skip when this manager is also the final approver who completed
            // it — the final-approver loop below already surfaces it, and
            // only that final-approved outcome should show, not both.
            const alsoFinalApprover = a.final_decision?.submitted_by_user_id === me?.id;
            if (!alsoFinalApprover) {
              completed.push({ ...base, status: a.status, statusLabel: 'Completed', completedAt: a.final_decided_at || a.updated_at || a.created_at });
            }
          } else if (a.status === 'rejected') {
            rejected.push({ ...base, status: a.status, statusLabel: 'Rejected', completedAt: a.final_decided_at || a.updated_at || a.created_at });
          } else if (a.status === 'closed') {
            closed.push({ ...base, status: a.status, statusLabel: 'Closed', completedAt: a.closed_at || a.updated_at || a.created_at });
          }
        });
      }

      // Final approver
      if (apprApproverR.status === 'fulfilled') {
        (apprApproverR.value.items || []).forEach(a => {
          const reasonLabel = APPRAISAL_REASON[a.reason] || a.reason;
          const cycles = (a.included_cycles || []).map(c => c.cycle_name).join(', ') || '—';
          const base = {
            id:          `appr-approver-${a.id}`,
            type:        'appraisal',
            typeLabel:   'Appraisal (Final)',
            typeColor:   '#ff6b6b',
            subject:     reasonLabel,
            detail:      cycles,
            userName:    apprUserName(a.employee),
            appraisalId: a.id,
          };
          if (a.status === 'pending_approver') {
            active.push({ ...base, status: a.status, statusLabel: 'Awaiting Final Decision', createdAt: a.created_at, action: 'Decide' });
          } else if (a.status === 'completed') {
            completed.push({ ...base, status: a.status, statusLabel: 'Completed', completedAt: a.final_decided_at || a.updated_at || a.created_at });
          } else if (a.rejected_by_stage === 'final_approver' && a.status === 'pending_manager') {
            // This approver rejected it back to the manager — same gap as
            // the manager's own rejection: without this branch it vanishes
            // from the approver's tabs the moment they reject.
            rejected.push({
              ...base, id: `appr-approver-rej-${a.id}`, status: a.status, statusLabel: 'Returned to Manager',
              rejectionReason: a.rejection_reason || null,
              completedAt: a.final_decided_at || a.updated_at || a.created_at,
            });
          } else if (a.rejected_by_stage === 'final_approver' && a.status === 'pending_employee') {
            rejected.push({
              ...base, id: `appr-approver-rej-${a.id}`, status: a.status, statusLabel: 'Returned to Employee',
              rejectionReason: a.rejection_reason || null,
              completedAt: a.final_decided_at || a.updated_at || a.created_at,
            });
          } else if (a.status === 'rejected') {
            rejected.push({ ...base, status: a.status, statusLabel: 'Rejected', completedAt: a.final_decided_at || a.updated_at || a.created_at });
          }
        });
      }

      // Newest first, matching the Completed/Rejected lists below
      active.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      // Most recently completed first
      completed.sort((a, b) => new Date(b.completedAt || 0) - new Date(a.completedAt || 0));
      rejected.sort((a, b) => new Date(b.completedAt || 0) - new Date(a.completedAt || 0));
      closed.sort((a, b) => new Date(b.completedAt || 0) - new Date(a.completedAt || 0));

      setItems(active);
      setCompletedItems(completed);
      setRejectedItems(rejected);
      setClosedItems(closed);
      onCountReady?.(active.length);
    }).finally(() => setLoading(false));
  }, [appId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { load(); }, [location.pathname]);

  const handleApproveGroup = async (grp) => {
    setActing(true); setError('');
    try { await api.approveBatchGoals(appId, { goal_ids: grp.goal_ids }); setExpandedId(null); load(); }
    catch (e) { setError(e.response?.data?.error || 'Failed to approve'); }
    finally { setActing(false); }
  };

  const handleRejectGroup = async () => {
    if (!rejectText.trim()) return setError('Please provide a rejection reason');
    setActing(true); setError('');
    try {
      await api.rejectBatchGoals(appId, { goal_ids: rejectModal.goal_ids, rejection_reason: rejectText });
      setRejectModal(null); setRejectText(''); load();
    }
    catch (e) { setError(e.response?.data?.error || 'Failed to reject'); }
    finally { setActing(false); }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Delete this self-rating review? This cannot be undone.')) return;
    setDeletingId(reviewId);
    try {
      await api.deletePerformanceReview(appId, reviewId);
      load();
    }
    catch (e) { setError(e.response?.data?.error || 'Failed to delete review'); }
    finally { setDeletingId(null); }
  };

  const FILTER_OPTS = [
    { val: 'all',           label: 'All Work' },
    { val: 'self-rating',   label: 'Self Rating' },
    { val: 'goal-approval', label: 'Goal Approvals' },
    { val: 'team-review',   label: 'Team Reviews' },
    { val: 'final-approval',label: 'Final Approvals' },
    { val: 'appraisal',     label: 'Appraisals' },
  ];

  const typeColorMap = { 'self-rating': '#f0a93b', 'goal-approval': '#ff6b6b', 'team-review': '#6f7bff', 'final-approval': '#06b6d4', 'appraisal': '#ff6b6b' };
  const sq = (searchQuery || '').toLowerCase().trim();
  const matchesSq = (i) => !sq || [i.subject, i.detail, i.userName, i.typeLabel, i.statusLabel].some(f => (f || '').toLowerCase().includes(sq));
  const filtered = (filter === 'all' ? items : items.filter(i => i.type === filter)).filter(matchesSq);
  const completedFiltered = completedItems.filter(matchesSq);
  const rejectedFiltered  = rejectedItems.filter(matchesSq);
  const closedFiltered    = closedItems.filter(matchesSq);

  const totalActivePages    = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const totalCompletedPages = Math.max(1, Math.ceil(completedFiltered.length / PAGE_SIZE));
  const totalRejectedPages  = Math.max(1, Math.ceil(rejectedFiltered.length / PAGE_SIZE));
  const totalClosedPages    = Math.max(1, Math.ceil(closedFiltered.length / PAGE_SIZE));

  const safeActivePage    = Math.min(activePage, totalActivePages);
  const safeCompletedPage = Math.min(completedPage, totalCompletedPages);
  const safeRejectedPage  = Math.min(rejectedPage, totalRejectedPages);
  const safeClosedPage    = Math.min(closedPage, totalClosedPages);

  const pagedActive    = filtered.slice((safeActivePage - 1) * PAGE_SIZE, safeActivePage * PAGE_SIZE);
  const pagedCompleted = completedFiltered.slice((safeCompletedPage - 1) * PAGE_SIZE, safeCompletedPage * PAGE_SIZE);
  const pagedRejected  = rejectedFiltered.slice((safeRejectedPage - 1) * PAGE_SIZE, safeRejectedPage * PAGE_SIZE);
  const pagedClosed    = closedFiltered.slice((safeClosedPage - 1) * PAGE_SIZE, safeClosedPage * PAGE_SIZE);

  const Pager = ({ page, total, onChange, count, pageSize }) => {
    if (total <= 1) return null;
    const pageNums = Array.from({ length: total }, (_, i) => i + 1)
      .filter(n => n === 1 || n === total || Math.abs(n - page) <= 1)
      .reduce((acc, n, i, arr) => {
        if (i > 0 && n - arr[i - 1] > 1) acc.push('…');
        acc.push(n);
        return acc;
      }, []);
    return (
      <div className="tl-pagination tl-pagination--top">
        {count != null && pageSize != null && (
          <span className="tl-page-info">
            {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, count)} of {count} item{count !== 1 ? 's' : ''}
          </span>
        )}
        <div className="tl-page-btns">
          <button className="tl-page-btn" disabled={page === 1} onClick={() => onChange(page - 1)}>← Prev</button>
          {pageNums.map((n, i) =>
            n === '…'
              ? <span key={`ellipsis-${i}`} className="tl-page-ellipsis">…</span>
              : <button key={n} className={`tl-page-num${n === page ? ' active' : ''}`} onClick={() => onChange(n)}>{n}</button>
          )}
          <button className="tl-page-btn" disabled={page === total} onClick={() => onChange(page + 1)}>Next →</button>
        </div>
      </div>
    );
  };

  // Each column has a floor and shares the spare width (fr), so the grid
  // stretches on wide screens; below the floors the card scrolls sideways
  // (.ph-table-scroll) instead of squeezing or clipping columns.
  const COL      = '44px minmax(120px, 1.3fr) minmax(100px, 1fr) minmax(150px, 2fr) minmax(110px, 1.1fr) 64px minmax(110px, 1fr) minmax(130px, 1fr)';
  const COL_DONE = '44px minmax(120px, 1.3fr) minmax(100px, 1fr) minmax(150px, 2fr) minmax(110px, 1.1fr) minmax(110px, 1fr) minmax(100px, 0.9fr) minmax(150px, 1fr)';

  const subTabBtn = (key, label, cnt) => (
    <button key={key} onClick={() => setMyWorkTab(key)} style={{
      padding: '6px 16px', borderRadius: 20, cursor: 'pointer',
      border: `1.5px solid ${myWorkTab === key ? 'var(--brand)' : 'var(--border)'}`,
      background: myWorkTab === key ? 'var(--brand)' : 'var(--bg-card)',
      color: myWorkTab === key ? '#fff' : 'var(--text-secondary)',
      fontWeight: myWorkTab === key ? 700 : 400, fontSize: 13,
      display: 'inline-flex', alignItems: 'center', gap: 6,
    }}>
      {label}
      <span style={{
        background: myWorkTab === key ? 'rgba(255,255,255,0.25)' : 'var(--bg-muted)',
        color: myWorkTab === key ? '#fff' : 'var(--text-primary)',
        borderRadius: 8, padding: '0 6px', fontSize: 11, fontWeight: 700,
      }}>{cnt}</span>
    </button>
  );

  return (
    <>
      {error && <div className="error-message" style={{ marginBottom: 12 }}>{error}</div>}

      <SectionTitle
        title="My Work"
        subtitle="Active tasks and completed history in one place"
      />

      {/* Active / Completed / Rejected sub-tab toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {subTabBtn('active',    '⚡ Active',    items.length)}
        {subTabBtn('completed', '✓ Completed',  completedItems.length)}
        {subTabBtn('rejected',  '✕ Rejected',   rejectedItems.length)}
        {subTabBtn('closed',    '🔒 Closed',    closedItems.length)}
      </div>

      {/* ── ACTIVE TAB ─────────────────────────────────────────────────── */}
      {myWorkTab === 'active' && (
        <>
          {/* Filter chips */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
            {FILTER_OPTS.map(opt => {
              const cnt   = opt.val === 'all' ? items.length : items.filter(i => i.type === opt.val).length;
              const col   = typeColorMap[opt.val] || 'var(--text-secondary)';
              const isAct = filter === opt.val;
              return (
                <button key={opt.val} onClick={() => setFilter(opt.val)} style={{
                  padding: '5px 13px', borderRadius: 16, border: `1px solid ${isAct ? col : 'var(--border)'}`,
                  background: isAct ? col : 'var(--bg-card)', color: isAct ? '#fff' : 'var(--text-secondary)',
                  fontWeight: isAct ? 700 : 400, fontSize: 12.5, cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                }}>
                  {opt.label}
                  <span style={{
                    background: isAct ? 'rgba(255,255,255,0.3)' : 'var(--bg-muted)',
                    color: isAct ? '#fff' : 'var(--text-primary)',
                    borderRadius: 8, padding: '0 5px', fontSize: 11, fontWeight: 700,
                  }}>{cnt}</span>
                </button>
              );
            })}
          </div>

          {loading ? <div className="loading">Loading…</div> :
           filtered.length === 0 ? (
             <EmptyState icon="⚡" title="All caught up!" subtitle="No pending tasks — you're on top of everything" />
           ) : (
            <>
            <Pager page={safeActivePage} total={totalActivePages} onChange={p => setActivePage(p)} count={filtered.length} pageSize={PAGE_SIZE} />
            <div className="ph-table-scroll" style={{ '--ph-min-w': '960px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10 }}>
              <div style={{
                display: 'grid', gridTemplateColumns: COL,
                background: 'var(--bg-muted)', borderBottom: '2px solid var(--border)',
                padding: '10px 16px', gap: 12, alignItems: 'center',
              }}>
                {['Sl No', 'Employee Name', 'Type', 'Review Cycle', 'Role', 'Days', 'Status', 'Action'].map(h => (
                  <div key={h} style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</div>
                ))}
              </div>
              <div style={{ maxHeight:'calc(100vh - 360px)', overflowY:'auto' }}>
              {pagedActive.map((item, idx) => {
                const globalIdx = (safeActivePage - 1) * PAGE_SIZE + idx;
                const days     = daysPending(item.createdAt);
                const expanded = expandedId === item.id;
                const isGoal   = item.type === 'goal-approval';

                return (
                  <div key={item.id}>
                    <div style={{
                      display: 'grid', gridTemplateColumns: COL,
                      padding: '12px 16px', gap: 12, alignItems: 'center',
                      borderBottom: expanded ? 'none' : '1px solid var(--border)',
                      background: expanded ? 'var(--bg-card-hover)' : 'transparent',
                      transition: 'background 0.1s',
                    }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>{globalIdx + 1}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.userName}>
                        {item.userName || '—'}
                      </div>
                      <div>
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 10,
                          background: item.typeColor + '18', color: item.typeColor, whiteSpace: 'nowrap',
                        }}>{item.typeLabel}</span>
                      </div>
                      <div style={{ overflow: 'hidden' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.subject}>
                          {item.subject}
                        </div>
                        {item.isReturned && item.rejectionReason && (
                          <div style={{ fontSize: 11, color: 'var(--neg)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.rejectionReason}>
                            ↩ {item.rejectionReason}
                          </div>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.detail}>
                        {item.detail}
                      </div>
                      <DaysBadge days={days} />
                      <StatusPill status={item.isReturned ? 'rejected' : item.status} label={item.statusLabel} />
                      <div>
                        {isGoal ? (
                          <button
                            onClick={() => setExpandedId(expanded ? null : item.id)}
                            style={{
                              padding: '6px 12px', borderRadius: 7,
                              border: `1px solid ${expanded ? '#06b6d4' : 'var(--border)'}`,
                              background: expanded ? 'var(--brand-light)' : 'var(--bg-card)',
                              color: expanded ? 'var(--brand)' : 'var(--text-primary)',
                              fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                            }}
                          >
                            {expanded ? '▲ Close' : 'Review ▼'}
                          </button>
                        ) : item.type === 'appraisal' ? (
                          <button
                            onClick={() => { setAppraisalId(item.appraisalId); setAppraisalViewOnly(false); }}
                            style={{
                              padding: '6px 12px', borderRadius: 7, border: 'none',
                              background: 'var(--neg)', color: '#fff',
                              fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                            }}
                          >
                            {item.action} →
                          </button>
                        ) : (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <button
                              onClick={() => navigate(item.navigateTo)}
                              style={{
                                padding: '6px 12px', borderRadius: 7, border: 'none',
                                background: 'var(--brand)', color: '#fff',
                                fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                              }}
                            >
                              {item.action} →
                            </button>
                            {item.type === 'self-rating' && (
                              <button
                                disabled={deletingId === item.reviewId}
                                onClick={() => handleDeleteReview(item.reviewId)}
                                title="Delete review"
                                style={{
                                  padding: '6px 8px', borderRadius: 7, border: '1px solid var(--neg)',
                                  background: 'transparent', color: 'var(--neg)',
                                  fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                }}
                              >✕</button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Goal approval expanded panel — shows all goals in group */}
                    {expanded && isGoal && (
                      <div style={{
                        padding: '14px 20px 16px', background: 'var(--bg-card-hover)',
                        borderBottom: '1px solid var(--border)', borderTop: '1px solid var(--border)',
                      }}>
                        <div style={{ fontSize: 13, color: 'var(--text-primary)', marginBottom: 10 }}>
                          Reviewing <strong>{item.goalGroup.goals.length} goal{item.goalGroup.goals.length !== 1 ? 's' : ''}</strong> for{' '}
                          <strong>{item.detail}</strong>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
                          {item.goalGroup.goals.map((g, i) => {
                            const gExpanded = expandedGoalId === g.id;
                            return (
                              <div key={g.id} style={{ background: 'var(--bg-muted)', borderRadius: 7, overflow: 'hidden', border: '1px solid var(--border)' }}>
                                <div
                                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px', cursor: 'pointer' }}
                                  onClick={() => setExpandedGoalId(gExpanded ? null : g.id)}
                                >
                                  <span style={{ minWidth: 20, height: 20, borderRadius: '50%', background: '#dbeafe', color: '#1d4ed8', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</span>
                                  <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{g.goal_name}</span>
                                  <span style={{ fontSize: 12, fontWeight: 600, color: '#059669' }}>{g.weightage}%</span>
                                  <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 4 }}>{gExpanded ? '▲' : '▼'}</span>
                                </div>
                                {gExpanded && (
                                  <div style={{ padding: '8px 12px 10px', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    {g.description && (
                                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{g.description}</div>
                                    )}
                                    {(g.target_l1 || g.target_l2 || g.target_l3 || g.target_l4 || g.target_l5) && (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        {[['L1', g.target_l1], ['L2', g.target_l2], ['L3', g.target_l3], ['L4', g.target_l4], ['L5', g.target_l5]].filter(([, v]) => v).map(([lbl, val]) => (
                                          <div key={lbl} style={{ display: 'flex', gap: 8, padding: '4px 8px', background: 'var(--bg-card)', borderRadius: 5, border: '1px solid var(--border)', fontSize: 12 }}>
                                            <strong style={{ minWidth: 22 }}>{lbl}</strong>
                                            <span>{val}</span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    {!g.description && !g.target_l1 && !g.target_l2 && !g.target_l3 && !g.target_l4 && !g.target_l5 && (
                                      <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>No description or targets set.</div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            disabled={acting}
                            onClick={() => handleApproveGroup(item.goalGroup)}
                            style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid #4ad295', background: 'rgba(74,210,149,0.1)', color: '#4ad295', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
                          >✓ Approve All</button>
                          <button
                            disabled={acting}
                            onClick={() => { setRejectModal({ goal_ids: item.goalGroup.goal_ids, groupName: item.detail, count: item.goalGroup.goals.length }); setError(''); }}
                            style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid #ff6b6b', background: 'rgba(255,107,107,0.1)', color: '#ff6b6b', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
                          >✕ Reject All</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              </div>{/* end scroll */}
            </div>
            </>
           )}
        </>
      )}

      {/* ── COMPLETED TAB ───────────────────────────────────────────────── */}
      {myWorkTab === 'completed' && (
        <>
          {loading ? <div className="loading">Loading…</div> :
           completedItems.length === 0 ? (
             <EmptyState icon="✓" title="No completed items yet" subtitle="Completed reviews will appear here once workflows are finished" />
           ) : (
            <>
            <Pager page={safeCompletedPage} total={totalCompletedPages} onChange={p => setCompletedPage(p)} count={completedFiltered.length} pageSize={PAGE_SIZE} />
            <div className="ph-table-scroll" style={{ '--ph-min-w': '960px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10 }}>
              <div style={{
                display: 'grid', gridTemplateColumns: COL_DONE,
                background: 'var(--bg-muted)', borderBottom: '2px solid var(--border)',
                padding: '10px 16px', gap: 12, alignItems: 'center',
              }}>
                {['Sl No', 'Employee Name', 'Type', 'Review Cycle', 'Role', 'Completed On', 'Status', 'Actions'].map(h => (
                  <div key={h} style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</div>
                ))}
              </div>
              <div style={{ maxHeight:'calc(100vh - 360px)', overflowY:'auto' }}>
              {pagedCompleted.map((item, idx) => {
                const globalIdx = (safeCompletedPage - 1) * PAGE_SIZE + idx;
                return (
                <div key={item.id} style={{
                  display: 'grid', gridTemplateColumns: COL_DONE,
                  padding: '12px 16px', gap: 12, alignItems: 'center',
                  borderBottom: '1px solid var(--border)',
                }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>{globalIdx + 1}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.userName}>
                    {item.userName || '—'}
                  </div>
                  <div>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 10,
                      background: item.typeColor + '18', color: item.typeColor, whiteSpace: 'nowrap',
                    }}>{item.typeLabel}</span>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.subject}>
                    {item.subject}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.detail}>
                    {item.detail}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {item.completedAt ? new Date(item.completedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                  </div>
                  <StatusPill status={item.status} label={item.statusLabel} />
                  <div style={{ display: 'flex', gap: 6 }}>
                    {item.type === 'appraisal' ? (
                      <button
                        onClick={() => { setAppraisalId(item.appraisalId); setAppraisalViewOnly(!!item.viewOnly); }}
                        style={{ padding: '5px 11px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                      >View</button>
                    ) : (
                      <>
                        <button
                          onClick={() => navigate(item.navigateTo)}
                          style={{ padding: '5px 11px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                        >View</button>
                        <button
                          onClick={() => downloadReviewPDF(item)}
                          title="Download PDF report"
                          disabled={pdfLoading === item.reviewId}
                          style={{ padding: '5px 11px', borderRadius: 7, border: '1px solid var(--info)', background: 'var(--brand-light)', color: 'var(--brand)', fontSize: 12, fontWeight: 600, cursor: pdfLoading === item.reviewId ? 'wait' : 'pointer', opacity: pdfLoading === item.reviewId ? 0.7 : 1 }}
                        >{pdfLoading === item.reviewId ? '…' : 'PDF ↓'}</button>
                      </>
                    )}
                  </div>
                </div>
                );
              })}
              </div>{/* end scroll */}
            </div>
            </>
           )}
        </>
      )}

      {/* ── REJECTED TAB ────────────────────────────────────────────────── */}
      {(myWorkTab === 'rejected' || myWorkTab === 'closed') && (
        <>
          {loading ? <div className="loading">Loading…</div> :
           (myWorkTab === 'closed' ? closedItems : rejectedItems).length === 0 ? (
             myWorkTab === 'closed'
               ? <EmptyState icon="🔒" title="No closed items" subtitle="Reviews and appraisals closed by an admin will appear here" />
               : <EmptyState icon="✕" title="No rejected reviews" subtitle="Reviews that were rejected will appear here" />
           ) : (
            <>
            <Pager
              page={myWorkTab === 'closed' ? safeClosedPage : safeRejectedPage}
              total={myWorkTab === 'closed' ? totalClosedPages : totalRejectedPages}
              onChange={p => (myWorkTab === 'closed' ? setClosedPage(p) : setRejectedPage(p))}
              count={(myWorkTab === 'closed' ? closedFiltered : rejectedFiltered).length} pageSize={PAGE_SIZE} />
            <div className="ph-table-scroll" style={{ '--ph-min-w': '960px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10 }}>
              <div style={{
                display: 'grid', gridTemplateColumns: COL_DONE,
                background: 'var(--bg-muted)', borderBottom: '2px solid var(--border)',
                padding: '10px 16px', gap: 12, alignItems: 'center',
              }}>
                {['Sl No', 'Employee Name', 'Type', 'Review Cycle', 'Role', myWorkTab === 'closed' ? 'Closed On' : 'Rejected On', 'Status', 'Actions'].map(h => (
                  <div key={h} style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</div>
                ))}
              </div>
              <div style={{ maxHeight:'calc(100vh - 360px)', overflowY:'auto' }}>
              {(myWorkTab === 'closed' ? pagedClosed : pagedRejected).map((item, idx) => {
                const globalIdx = ((myWorkTab === 'closed' ? safeClosedPage : safeRejectedPage) - 1) * PAGE_SIZE + idx;
                return (
                <div key={item.id} style={{
                  display: 'grid', gridTemplateColumns: COL_DONE,
                  padding: '12px 16px', gap: 12, alignItems: 'center',
                  borderBottom: '1px solid var(--border)',
                }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>{globalIdx + 1}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.userName}>
                    {item.userName || '—'}
                  </div>
                  <div>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 10, background: item.typeColor + '18', color: item.typeColor, whiteSpace: 'nowrap' }}>{item.typeLabel}</span>
                  </div>
                  <div style={{ overflow: 'hidden' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.subject}>{item.subject}</div>
                    {item.rejectionReason && (
                      <div style={{ fontSize: 11, color: 'var(--neg)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.rejectionReason}>↩ {item.rejectionReason}</div>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.detail}>{item.detail}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {item.completedAt ? new Date(item.completedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                  </div>
                  <StatusPill status={item.status} label={item.statusLabel} />
                  <div>
                    <button
                      onClick={() => { if (item.type === 'appraisal') { setAppraisalId(item.appraisalId); setAppraisalViewOnly(false); } else navigate(item.navigateTo); }}
                      style={{ padding: '5px 11px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                    >View</button>
                  </div>
                </div>
                );
              })}
              </div>{/* end scroll */}
            </div>
            </>
           )}
        </>
      )}

      {/* Appraisal detail modal */}
      {appraisalId && (
        <AppraisalDetail
          appraisalId={appraisalId}
          viewOnly={appraisalViewOnly}
          onClose={() => { setAppraisalId(null); setAppraisalViewOnly(false); }}
          onRefresh={load}
        />
      )}

      {/* Goal detail modal */}
      {viewGoalData && (
        <Modal open title="Goal Details" onClose={() => setViewGoalData(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 14 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', marginBottom: 2 }}>Goal</div>
              <div style={{ fontWeight: 600, fontSize: 16 }}>{viewGoalData.goal_name}</div>
            </div>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', marginBottom: 2 }}>Employee</div>
                <div>{viewGoalData.user?.first_name || ''} {viewGoalData.user?.last_name || viewGoalData.user?.email || 'Unknown'}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', marginBottom: 2 }}>Role</div>
                <div>{viewGoalData.role_title || '—'}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', marginBottom: 2 }}>Weightage</div>
                <div style={{ fontWeight: 600 }}>{viewGoalData.weightage}%</div>
              </div>
            </div>
            {viewGoalData.description && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', marginBottom: 4 }}>Description</div>
                <div style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>{viewGoalData.description}</div>
              </div>
            )}
            {(viewGoalData.target_l1 || viewGoalData.target_l2 || viewGoalData.target_l3 || viewGoalData.target_l4 || viewGoalData.target_l5) && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', marginBottom: 8 }}>Targets</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {[['L1', viewGoalData.target_l1], ['L2', viewGoalData.target_l2], ['L3', viewGoalData.target_l3], ['L4', viewGoalData.target_l4], ['L5', viewGoalData.target_l5]].filter(([, v]) => v).map(([label, value]) => (
                    <div key={label} style={{ display: 'flex', gap: 10, padding: '6px 10px', background: 'var(--bg-muted)', borderRadius: 6, border: '1px solid var(--border)' }}>
                      <span style={{ fontWeight: 700, minWidth: 24 }}>{label}</span>
                      <span>{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
              <button className="btn-secondary" onClick={() => setViewGoalData(null)}>Close</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Reject goals modal */}
      {rejectModal && (
        <Modal open title="Reject Goals" onClose={() => { setRejectModal(null); setRejectText(''); setError(''); }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <p style={{ margin: 0, fontSize: 14 }}>
              Rejecting <strong>{rejectModal.count}</strong> goal{rejectModal.count !== 1 ? 's' : ''} for{' '}
              <strong>{rejectModal.groupName}</strong>
            </p>
            <div>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Reason *</label>
              <textarea
                rows={4}
                value={rejectText}
                onChange={e => setRejectText(e.target.value)}
                placeholder="Explain why these goals are being rejected…"
                style={{ width: '100%', boxSizing: 'border-box', borderRadius: 8, border: '1px solid var(--border)', padding: '8px 12px', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }}
              />
            </div>
            {error && <div className="error-message">{error}</div>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => { setRejectModal(null); setRejectText(''); }} style={cancelBtnStyle}>Cancel</button>
              <button
                style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: 'var(--neg)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
                onClick={handleRejectGroup}
                disabled={acting}
              >
                {acting ? 'Rejecting…' : 'Reject All Goals'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── Goal Detail Modal ────────────────────────────────────────────────────────

function GoalDetailModal({ goal, onClose, onEdit, canEdit }) {
  if (!goal) return null;
  const color = STATUS_COLOR[goal.status] || 'var(--text-secondary)';
  return (
    <Modal open title="Goal Details" onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h3 style={{ margin: 0, fontSize: 16, flex: 1 }}>{goal.goal_name}</h3>
          <StatusPill status={goal.status} />
        </div>
        {goal.description && (
          <div>
            <div style={detailLabel}>Description</div>
            <div style={{ fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.6 }}>{goal.description}</div>
          </div>
        )}
        <div>
          <div style={detailLabel}>Weightage</div>
          <div style={{ fontSize: 22, fontWeight: 700, color }}>{goal.weightage}%</div>
        </div>
        {[1,2,3,4,5].some(l => goal[`target_l${l}`]) && (
          <div>
            <div style={detailLabel}>Level Targets</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[1,2,3,4,5].map(l => goal[`target_l${l}`] ? (
                <div key={l} style={{ display: 'flex', gap: 10, fontSize: 13, alignItems: 'flex-start' }}>
                  <span style={{ fontWeight: 700, color: 'var(--text-muted)', minWidth: 28, flexShrink: 0 }}>L{l}</span>
                  <span style={{ color: 'var(--text-primary)' }}>{goal[`target_l${l}`]}</span>
                </div>
              ) : null)}
            </div>
          </div>
        )}
        {goal.rejection_reason && (
          <div style={{ background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.25)', borderRadius: 8, padding: '10px 14px' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--neg)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Rejection Reason</div>
            <div style={{ fontSize: 13, color: 'var(--neg)' }}>{goal.rejection_reason}</div>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4 }}>
          {canEdit && <button className="btn-primary" onClick={onEdit}>Edit Goal</button>}
          <button onClick={onClose} style={cancelBtnStyle}>Close</button>
        </div>
      </div>
    </Modal>
  );
}

const detailLabel = { fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 };

// ─── My Goals ─────────────────────────────────────────────────────────────────

function MyGoals({ appId }) {
  const api = useApi();
  const { canWrite, myRole, me } = useWorkspace();
  const isManagerOrAdmin = canWrite || myRole === 'admin' || myRole === 'super_admin';

  const [goals,        setGoals]        = useState([]);
  const [roles,        setRoles]        = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [showForm,     setShowForm]     = useState(false);
  const [editingGoal,  setEditingGoal]  = useState(null);
  const [viewingGoal,  setViewingGoal]  = useState(null);
  const [acting,       setActing]       = useState(false);
  const [error,        setError]        = useState('');
  const [activeReview, setActiveReview] = useState(null);
  const [goalsPage,    setGoalsPage]    = useState(1);
  const GOALS_PAGE_SIZE = usePageSize(130, 360);

  useEffect(() => {
    if (!isManagerOrAdmin && !me?.id) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.roles.list(appId),
      api.listGoals(appId),
      me?.id ? api.listAssignmentsForUser(appId, me.id) : Promise.resolve(null),
    ])
      .then(([rd, gd, assignmentData]) => {
        if (cancelled) return;
        let r = rd.items || [];
        const myRoleIds = (assignmentData?.items || []).map(a => a.role_id);
        if (!isManagerOrAdmin && assignmentData) {
          const mine = new Set(myRoleIds);
          r = r.filter(role => mine.has(role.id));
        }
        setRoles(r);
        // Open on the role the user actually has goals under, then their own
        // assigned role. Admins see every org role, so falling straight to
        // r[0] picked an arbitrary role and hid their goals.
        const goalRoleId = (gd.items || []).find(g => r.some(role => role.id === g.role_id))?.role_id;
        const assignedRoleId = myRoleIds.find(id => r.some(role => role.id === id));
        const initial = goalRoleId ?? assignedRoleId ?? r[0]?.id;
        if (initial) setSelectedRole(initial);
      })
      .catch(() => setError('Failed to load data'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [appId, me?.id, isManagerOrAdmin]);

  useEffect(() => {
    if (!selectedRole) { setGoals([]); setActiveReview(null); return; }
    Promise.all([
      api.listGoals(appId, { role_id: selectedRole }),
      api.listPerformanceReviews(appId, { role_id: selectedRole }),
    ]).then(([gd, rd]) => {
      setGoals(gd.items || []);
      const inProgress = (rd.items || []).find(r =>
        ['pending_self', 'pending_manager', 'pending_approver'].includes(r.status)
      );
      setActiveReview(inProgress || null);
    }).catch(() => setError('Failed to load goals'));
  }, [selectedRole, appId]);

  const reload = () =>
    Promise.all([
      api.listGoals(appId, { role_id: selectedRole }),
      api.listPerformanceReviews(appId, { role_id: selectedRole }),
    ]).then(([gd, rd]) => {
      setGoals(gd.items || []);
      const inProgress = (rd.items || []).find(r =>
        ['pending_self', 'pending_manager', 'pending_approver'].includes(r.status)
      );
      setActiveReview(inProgress || null);
    });

  const handleSubmitAll = async () => {
    const pending = goals.filter(g => g.status === 'pending');
    if (!window.confirm(`Submit all ${pending.length} pending goal(s) for approval?`)) return;
    setActing(true);
    try {
      await Promise.all(pending.map(g => api.submitGoalForApproval(appId, g.id)));
      reload();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit');
    } finally { setActing(false); }
  };

  const totalWeightage = goals.reduce((s, g) => s + (g.weightage || 0), 0);
  const hasPending     = goals.some(g => g.status === 'pending');
  const canSubmit      = totalWeightage === 100 && hasPending;

  if (loading) return <div className="loading">Loading…</div>;

  return (
    <>
      {error && <div className="error-message" style={{ marginBottom: 12 }}>{error}</div>}

      <SectionTitle
        title="My Goals"
        subtitle="Track your goals and get them approved before the review period begins"
        action={
          <button
            className="btn-primary"
            disabled={!selectedRole}
            title={!selectedRole ? 'No role assigned to you yet' : ''}
            onClick={() => {
              if (!selectedRole) {
                setError('You have no role assigned. Ask your admin to assign a role before creating goals.');
                return;
              }
              setEditingGoal(null); setShowForm(true); setError('');
            }}
          >
            + Create Goal
          </button>
        }
      />

      {canSubmit && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <button
            style={{
              padding: '8px 18px', borderRadius: 8, border: 'none',
              background: 'var(--pos)', color: '#fff', fontWeight: 600, fontSize: 13,
              cursor: 'pointer',
            }}
            onClick={handleSubmitAll}
            disabled={acting}
          >
            {acting ? 'Submitting…' : '↑ Submit All for Approval'}
          </button>
        </div>
      )}

      {/* Weightage bar */}
      {goals.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Total Weightage</span>
            <span style={{
              fontSize: 13, fontWeight: 700,
              color: totalWeightage === 100 ? '#4ad295' : totalWeightage > 100 ? '#ff6b6b' : '#f0a93b',
            }}>
              {totalWeightage.toFixed(1)}% / 100%
              {totalWeightage === 100 && ' ✓'}
              {totalWeightage !== 100 && totalWeightage > 0 && ' — must equal 100% to submit'}
            </span>
          </div>
          <div style={{ height: 6, background: 'var(--bg-muted)', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 6,
              width: `${Math.min(totalWeightage, 100)}%`,
              background: totalWeightage === 100 ? '#4ad295' : totalWeightage > 100 ? '#ff6b6b' : '#f0a93b',
              transition: 'width 0.3s',
            }} />
          </div>
        </div>
      )}

      {goals.length === 0 ? (
        <EmptyState icon="🎯" title="No goals yet" subtitle="Create your first goal to get started" />
      ) : (() => {
        const totalGoalPages = Math.max(1, Math.ceil(goals.length / GOALS_PAGE_SIZE));
        const safeGoalsPage  = Math.min(goalsPage, totalGoalPages);
        const pagedGoals     = goals.slice((safeGoalsPage - 1) * GOALS_PAGE_SIZE, safeGoalsPage * GOALS_PAGE_SIZE);
        return (<>
        {totalGoalPages > 1 && (
          <div className="tl-pagination tl-pagination--top" style={{ marginBottom: 12 }}>
            <span className="tl-page-info">{(safeGoalsPage-1)*GOALS_PAGE_SIZE+1}–{Math.min(safeGoalsPage*GOALS_PAGE_SIZE, goals.length)} of {goals.length} goals</span>
            <div className="tl-page-btns">
              <button className="tl-page-btn" disabled={safeGoalsPage===1} onClick={() => setGoalsPage(p=>p-1)}>← Prev</button>
              {Array.from({length:totalGoalPages},(_,i)=>i+1).filter(n=>n===1||n===totalGoalPages||Math.abs(n-safeGoalsPage)<=1).reduce((acc,n,i,arr)=>{if(i>0&&n-arr[i-1]>1)acc.push('…');acc.push(n);return acc;},[]).map((n,i)=>n==='…'?<span key={`e${i}`} className="tl-page-ellipsis">…</span>:<button key={n} className={`tl-page-num${n===safeGoalsPage?' active':''}`} onClick={()=>setGoalsPage(n)}>{n}</button>)}
              <button className="tl-page-btn" disabled={safeGoalsPage===totalGoalPages} onClick={() => setGoalsPage(p=>p+1)}>Next →</button>
            </div>
          </div>
        )}
        {pagedGoals.map((goal, i) => {
          const globalIdx = (safeGoalsPage - 1) * GOALS_PAGE_SIZE + i;
          const color   = STATUS_COLOR[goal.status] || 'var(--text-secondary)';
          const canEdit = ['pending', 'rejected', 'approved'].includes(goal.status) && !activeReview;
          const editLocked = ['pending', 'rejected', 'approved'].includes(goal.status) && !!activeReview;
          return (
            <Card key={goal.id} accent={color}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 }}>#{globalIdx + 1}</span>
                    <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>{goal.goal_name}</span>
                    <StatusPill status={goal.status} />
                    {goal.status === 'approved' && (
                      <span style={{ fontSize: 11, fontWeight: 600, color: goal.is_active ? '#4ad295' : '#94a3b8' }}>
                        {goal.is_active ? '● Active' : '○ Inactive'}
                      </span>
                    )}
                  </div>
                  {goal.description && (
                    <p style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{goal.description}</p>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 13, color: 'var(--text-muted)' }}>
                    <span>Weightage: <strong style={{ color: 'var(--text-primary)' }}>{goal.weightage}%</strong></span>
                    {goal.rejection_reason && (
                      <span style={{ color: 'var(--neg)' }}>✕ {goal.rejection_reason}</span>
                    )}
                  </div>
                  {editLocked && (
                    <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                      🔒 Editing locked — performance review in progress
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setViewingGoal(goal)}
                    style={{ ...ghostBtnStyle, fontSize: 13, color: 'var(--brand)' }}
                  >
                    View
                  </button>
                  {canEdit && (
                    <button
                      onClick={() => { setEditingGoal(goal); setShowForm(true); }}
                      style={{ ...ghostBtnStyle, fontSize: 13 }}
                    >
                      Edit
                    </button>
                  )}
                  {canEdit && goal.status === 'approved' && (
                    <button
                      onClick={async () => {
                        const action = goal.is_active ? 'deactivate' : 'activate';
                        if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} goal "${goal.goal_name}"?`)) return;
                        try { await api.toggleGoalActive(appId, goal.id); reload(); }
                        catch (err) { setError(err.response?.data?.error || `Failed to ${action} goal`); }
                      }}
                      style={{ ...ghostBtnStyle, fontSize: 13, color: goal.is_active ? 'var(--text-muted)' : 'var(--pos)' }}
                    >
                      {goal.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
        </>);
      })()}

      {showForm && (
        <Modal open title={editingGoal ? 'Edit Goal' : 'Create Goal'} onClose={() => setShowForm(false)}>
          <GoalForm
            appId={appId}
            goal={editingGoal}
            roleId={selectedRole}
            totalWeightage={editingGoal ? totalWeightage - (editingGoal.weightage || 0) : totalWeightage}
            onSave={(savedRoleId) => {
              setShowForm(false);
              if (savedRoleId && savedRoleId !== selectedRole) {
                setSelectedRole(savedRoleId);
              } else {
                reload();
              }
            }}
            onCancel={() => setShowForm(false)}
          />
        </Modal>
      )}

      {viewingGoal && (
        <GoalDetailModal
          goal={viewingGoal}
          canEdit={['pending', 'rejected'].includes(viewingGoal.status)}
          onClose={() => setViewingGoal(null)}
          onEdit={() => { setEditingGoal(viewingGoal); setViewingGoal(null); setShowForm(true); }}
        />
      )}
    </>
  );
}

// ─── Recognitions ─────────────────────────────────────────────────────────────

function Recognitions({ appId }) {
  const api       = useApi();
  const { me }    = useWorkspace();

  const [mainTab, setMainTab] = useState('appreciations'); // 'appreciations' | 'feedback'
  const [subTab,  setSubTab]  = useState('received');      // 'received' | 'given'

  const [appRecv,  setAppRecv]  = useState([]);
  const [appGiven, setAppGiven] = useState([]);
  const [fbRecv,   setFbRecv]   = useState([]);
  const [fbGiven,  setFbGiven]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [err,      setErr]      = useState('');
  const [recPage,  setRecPage]  = useState(1);
  const REC_PAGE_SIZE = usePageSize(110, 360);

  useEffect(() => {
    if (!me?.id) return;
    setLoading(true);
    Promise.allSettled([
      api.getAppreciationsForUser(appId, me.id),
      api.getReceivedFeedback(appId),
      api.getGivenFeedback(appId),
    ]).then(([appR, fbRR, fbGR]) => {
      if (appR.status  === 'fulfilled') { setAppRecv(appR.value.received || []); setAppGiven(appR.value.given || []); }
      if (fbRR.status  === 'fulfilled') setFbRecv(fbRR.value.items || []);
      if (fbGR.status  === 'fulfilled') setFbGiven(fbGR.value.items || []);
    }).catch(() => setErr('Failed to load recognitions'))
    .finally(() => setLoading(false));
  }, [appId, me?.id]);

  const uname = u => u ? ([u.first_name, u.last_name].filter(Boolean).join(' ') || u.email) : 'Unknown';

  const fmt = dt => dt ? new Date(dt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';

  // ── Tab buttons ──────────────────────────────────────────────────────────────

  const mainTabBtn = (key, icon, label, cnt) => (
    <button key={key} onClick={() => { setMainTab(key); setSubTab('received'); setRecPage(1); }} style={{
      padding: '8px 20px', borderRadius: 8, cursor: 'pointer',
      border: `1.5px solid ${mainTab === key ? 'var(--brand)' : 'var(--border)'}`,
      background: mainTab === key ? 'var(--brand)' : 'var(--bg-card)',
      color: mainTab === key ? '#fff' : 'var(--text-secondary)',
      fontWeight: mainTab === key ? 700 : 400, fontSize: 13.5,
      display: 'inline-flex', alignItems: 'center', gap: 7,
    }}>
      <span>{icon}</span>
      <span>{label}</span>
      <span style={{
        background: mainTab === key ? 'rgba(255,255,255,0.25)' : 'var(--bg-muted)',
        color: mainTab === key ? '#fff' : 'var(--text-primary)',
        borderRadius: 8, padding: '0 6px', fontSize: 11, fontWeight: 700,
      }}>{cnt}</span>
    </button>
  );

  const subTabBtn = (key, label, cnt) => (
    <button key={key} onClick={() => { setSubTab(key); setRecPage(1); }} style={{
      padding: '5px 14px', borderRadius: 16, cursor: 'pointer',
      border: `1px solid ${subTab === key ? 'var(--brand)' : 'var(--border)'}`,
      background: subTab === key ? 'var(--brand-light)' : 'var(--bg-card)',
      color: subTab === key ? 'var(--brand)' : 'var(--text-secondary)',
      fontWeight: subTab === key ? 700 : 400, fontSize: 13,
      display: 'inline-flex', alignItems: 'center', gap: 5,
    }}>
      {label}
      <span style={{
        background: subTab === key ? 'var(--brand)' : 'var(--bg-muted)',
        color: subTab === key ? '#fff' : 'var(--text-primary)',
        borderRadius: 8, padding: '0 5px', fontSize: 11, fontWeight: 700,
      }}>{cnt}</span>
    </button>
  );

  // ── Appreciation card ────────────────────────────────────────────────────────

  const AppCard = ({ a }) => {
    const badge = a.badge ? BADGE_META[a.badge] : null;
    return (
      <Card style={{ borderLeft: '4px solid var(--warn)', background: 'rgba(240, 169, 59, 0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          {badge ? (
            <div style={{ fontSize: 28, flexShrink: 0 }}>{badge.emoji}</div>
          ) : (
            <div style={{ fontSize: 28, flexShrink: 0 }}>🏆</div>
          )}
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
              <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>{uname(a.to_user)}</span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>recognised by</span>
              <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{uname(a.from_user)}</span>
              {badge && (
                <span style={{ background: 'rgba(240,169,59,0.15)', border: '1px solid rgba(240,169,59,0.3)', color: 'var(--warn)', borderRadius: 12, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>
                  {badge.label}
                </span>
              )}
            </div>
            <div style={{ fontSize: 14, color: 'var(--text-primary)', fontStyle: 'italic', lineHeight: 1.5 }}>"{a.message}"</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>{fmt(a.created_at)}</div>
          </div>
        </div>
      </Card>
    );
  };

  // ── Feedback card ────────────────────────────────────────────────────────────

  const handleDeleteFeedback = async (id) => {
    if (!window.confirm('Delete this feedback? This will remove it for all recipients.')) return;
    try {
      await api.deleteFeedback(appId, id);
      setFbGiven(prev => prev.filter(f => f.id !== id));
    } catch { setErr('Failed to delete feedback'); }
  };

  const FbCard = ({ f, isReceived }) => (
    <Card style={{ borderLeft: '4px solid var(--info)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ fontSize: 24, flexShrink: 0 }}>💬</div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            {isReceived ? (
              f.is_anonymous
                ? <span style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>Anonymous</span>
                : <><span style={{ fontSize: 12, color: 'var(--text-muted)' }}>From:</span>
                   <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{uname(f.from_user)}</span></>
            ) : (
              <><span style={{ fontSize: 12, color: 'var(--text-muted)' }}>To:</span>
               <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{uname(f.to_user)}</span></>
            )}
            {f.is_anonymous && !isReceived && (
              <span style={{ background: 'var(--bg-muted)', color: 'var(--text-secondary)', borderRadius: 12, padding: '2px 9px', fontSize: 11, fontWeight: 600 }}>Sent anonymously</span>
            )}
            {!isReceived && (
              <button onClick={() => handleDeleteFeedback(f.id)} style={{
                marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-muted)', fontSize: 13, padding: '2px 6px', borderRadius: 4,
              }} title="Delete feedback">🗑</button>
            )}
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.6 }}>{f.feedback_text}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>{fmt(f.created_at)}</div>
        </div>
      </div>
    </Card>
  );

  // ── Derived lists ────────────────────────────────────────────────────────────

  const currentApps = subTab === 'received' ? appRecv : appGiven;
  const currentFb   = subTab === 'received' ? fbRecv  : fbGiven;

  return (
    <>
      {err && <div className="error-message" style={{ marginBottom: 12 }}>{err}</div>}
      <SectionTitle title="Recognitions" subtitle="All-time appreciations and peer feedback" />

      {/* ── Main tabs ── */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        {mainTabBtn('appreciations', '🏆', 'Appreciations', appRecv.length + appGiven.length)}
        {mainTabBtn('feedback',      '💬', 'Feedback',      fbRecv.length  + fbGiven.length)}
      </div>

      {/* ── Sub-tabs ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {subTabBtn('received', 'Received', mainTab === 'appreciations' ? appRecv.length : fbRecv.length)}
        {subTabBtn('given',    'Given',    mainTab === 'appreciations' ? appGiven.length : fbGiven.length)}
      </div>

      {loading && <div className="loading">Loading…</div>}

      {/* ── Appreciations ── */}
      {!loading && mainTab === 'appreciations' && (() => {
        if (currentApps.length === 0) return <EmptyState icon="🏆" title={subTab === 'received' ? 'No appreciations received yet' : 'No appreciations given yet'} subtitle={subTab === 'received' ? 'Colleagues can recognise you from the Feed' : 'Go to Feed to appreciate a colleague'} />;
        const total = Math.max(1, Math.ceil(currentApps.length / REC_PAGE_SIZE));
        const safe  = Math.min(recPage, total);
        const paged = currentApps.slice((safe - 1) * REC_PAGE_SIZE, safe * REC_PAGE_SIZE);
        return (<>
          {total > 1 && (
            <div className="tl-pagination tl-pagination--top" style={{ marginBottom: 12 }}>
              <span className="tl-page-info">{(safe-1)*REC_PAGE_SIZE+1}–{Math.min(safe*REC_PAGE_SIZE, currentApps.length)} of {currentApps.length}</span>
              <div className="tl-page-btns">
                <button className="tl-page-btn" disabled={safe===1} onClick={() => setRecPage(p=>p-1)}>← Prev</button>
                {Array.from({length:total},(_,i)=>i+1).filter(n=>n===1||n===total||Math.abs(n-safe)<=1).reduce((acc,n,i,arr)=>{if(i>0&&n-arr[i-1]>1)acc.push('…');acc.push(n);return acc;},[]).map((n,i)=>n==='…'?<span key={`e${i}`} className="tl-page-ellipsis">…</span>:<button key={n} className={`tl-page-num${n===safe?' active':''}`} onClick={()=>setRecPage(n)}>{n}</button>)}
                <button className="tl-page-btn" disabled={safe===total} onClick={() => setRecPage(p=>p+1)}>Next →</button>
              </div>
            </div>
          )}
          {paged.map(a => <AppCard key={a.id} a={a} />)}
        </>);
      })()}

      {/* ── Feedback ── */}
      {!loading && mainTab === 'feedback' && (() => {
        if (currentFb.length === 0) return <EmptyState icon="💬" title={subTab === 'received' ? 'No feedback received yet' : 'No feedback given yet'} subtitle={subTab === 'received' ? 'Peer feedback sent to you will appear here' : 'Give feedback to colleagues from the Feed'} />;
        const total = Math.max(1, Math.ceil(currentFb.length / REC_PAGE_SIZE));
        const safe  = Math.min(recPage, total);
        const paged = currentFb.slice((safe - 1) * REC_PAGE_SIZE, safe * REC_PAGE_SIZE);
        return (<>
          {total > 1 && (
            <div className="tl-pagination tl-pagination--top" style={{ marginBottom: 12 }}>
              <span className="tl-page-info">{(safe-1)*REC_PAGE_SIZE+1}–{Math.min(safe*REC_PAGE_SIZE, currentFb.length)} of {currentFb.length}</span>
              <div className="tl-page-btns">
                <button className="tl-page-btn" disabled={safe===1} onClick={() => setRecPage(p=>p-1)}>← Prev</button>
                {Array.from({length:total},(_,i)=>i+1).filter(n=>n===1||n===total||Math.abs(n-safe)<=1).reduce((acc,n,i,arr)=>{if(i>0&&n-arr[i-1]>1)acc.push('…');acc.push(n);return acc;},[]).map((n,i)=>n==='…'?<span key={`e${i}`} className="tl-page-ellipsis">…</span>:<button key={n} className={`tl-page-num${n===safe?' active':''}`} onClick={()=>setRecPage(n)}>{n}</button>)}
                <button className="tl-page-btn" disabled={safe===total} onClick={() => setRecPage(p=>p+1)}>Next →</button>
              </div>
            </div>
          )}
          {paged.map(f => <FbCard key={f.id} f={f} isReceived={subTab === 'received'} />)}
        </>);
      })()}
    </>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function PerformanceHub() {
  const { appId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canWrite } = useWorkspace();
  const active = searchParams.get('tab') || 'overview';

  const setActive = (tab) => setSearchParams({ tab }, { replace: true });

  const TABS = [
    { key: 'overview',       label: 'Overview',       adminOnly: false },
    { key: 'mywork',         label: 'My Work',        adminOnly: false },
    { key: 'goals',          label: 'Goals',          adminOnly: false },
    { key: 'review-cycles',  label: 'Review Cycles',  adminOnly: true  },
    { key: 'appraisals',     label: 'Appraisals',     adminOnly: true  },
    { key: 'monitoring',     label: 'Monitoring',     adminOnly: true  },
  ].filter(t => !t.adminOnly || canWrite);

  return (
    <div style={{ width: '100%' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 className="page-title">Performance</h1>
        <p style={{ margin: '4px 0 0', fontSize: 15, color: 'var(--text-secondary)' }}>
          Overview · My Work · Goals{canWrite && ' · Monitoring · Appraisals · Review Cycles'}
        </p>
      </div>
      <div className="setup-tabs" role="tablist">
        {TABS.map(t => (
          <button key={t.key} className={`setup-tab${active === t.key ? ' active' : ''}`} onClick={() => setActive(t.key)} role="tab">
            {t.label}
          </button>
        ))}
      </div>
      {active === 'overview'      && <Overview appId={appId} onNavigate={setActive} onGoalsBadge={() => {}} />}
      {active === 'mywork'        && <MyWork   appId={appId} onCountReady={() => {}} />}
      {active === 'goals'         && <MyGoals  appId={appId} />}
      {active === 'monitoring'    && canWrite && <MonitoringDashboard appId={appId} />}
      {active === 'appraisals'    && canWrite && <AppraisalsTab appId={appId} />}
      {active === 'review-cycles' && canWrite && <PerformanceReviewCycles />}
    </div>
  );
}

// ─── Shared styles ────────────────────────────────────────────────────────────

const cancelBtnStyle = {
  padding: '8px 18px', border: '1px solid var(--border)', borderRadius: 8,
  background: 'var(--bg-card)', cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)',
};

const ghostBtnStyle = {
  background: 'none', border: 'none', cursor: 'pointer',
  padding: '5px 10px', borderRadius: 6, color: 'var(--text-secondary)', fontSize: 13,
};

